import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { useSettings } from "../context/SettingsContext";
import { api, setAdminToken } from "../lib/api";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, Package, ShoppingBag, Mail, Settings as SettingsIcon, X, Image as ImageIcon, Users, FileText, Layers, Eye, EyeOff, Tag, Power, Gift, CreditCard, Bell, ChevronDown, ChevronUp, Megaphone, Wallet, BarChart2, TrendingUp, Menu, Home, MessageSquare, Grid3x3, Star, Camera, Upload, RefreshCw } from "lucide-react";

const SECTIONS = {
  "Charts by Subject": ["Mathematics", "Language Arts", "Science", "Social Studies", "Mental Ability", "P.E.", "Music", "Integrated Studies"],
  "Class Decor": ["Borders", "Accents", "Door Sets", "Welcome Signs", "Motivational Charts & Banners"],
  "Teacher Essentials": ["Storage Bins", "Stickers", "Reward Charts & Buttons", "Reward Certificates"],
  "Graduation & Teacher Appreciation": [],
};
const GRADES = ["Kindergarten", "Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5", "Grade 6"];
const INTEGRATED_GRADES = ["Grade 1", "Grade 2", "Grade 3"];

const CATEGORY_KINDS = [
  { kind: "subject", label: "Charts by Subject", items: SECTIONS["Charts by Subject"] },
  { kind: "grade", label: "Shop by Grade", items: GRADES },
  { kind: "decor", label: "Class Decor", items: SECTIONS["Class Decor"] },
  { kind: "essentials", label: "Teacher Essentials", items: SECTIONS["Teacher Essentials"] },
];

const Login = ({ onLogin }) => {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/admin/login", { password: pw });
      setAdminToken(r.data.token);
      // Fetch admin name from verify
      let name = "Admin";
      try { const vr = await api.get("/admin/verify"); name = vr.data.admin_name || "Admin"; } catch {}
      onLogin(name);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 503 || status === 502 || !err?.response) {
        toast.error("Server is starting up — please wait 30 seconds and try again.");
      } else if (status === 401) {
        toast.error("Invalid password.");
      } else {
        toast.error("Login failed — please try again.");
      }
    }
    finally { setLoading(false); }
  };
  return (
    <div className="max-w-md mx-auto px-6 py-20" data-testid="admin-login">
      <div className="c4t-card">
        <h1 className="text-3xl font-['Fredoka'] font-bold">Admin Access</h1>
        <p className="c4t-desc mt-1 text-sm">Enter your admin password to manage the shop.</p>
        <form onSubmit={submit} className="mt-5 space-y-4">
          <div className="relative">
            <input
              type={showPw ? "text" : "password"}
              className="c4t-input pr-12"
              placeholder="Password"
              value={pw}
              onChange={e => setPw(e.target.value)}
              required
              data-testid="admin-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPw(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#455A64] hover:text-[#FF1F87]"
              aria-label={showPw ? "Hide password" : "Show password"}
              data-testid="admin-password-toggle"
            >
              {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          <button type="submit" disabled={loading} className="c4t-btn c4t-btn-pink w-full" data-testid="admin-login-btn">{loading ? "Signing in..." : "Sign In"}</button>
          <button type="button" onClick={async () => { try { await api.post("/admin/forgot-password"); toast.success("Password reset link sent to cheap4teach@gmail.com. Check your inbox."); } catch (e) { toast.error("Could not send reset email. Please try again."); } }} className="block w-full text-center text-sm text-[#455A64] hover:text-[#FF1F87] mt-3" data-testid="admin-forgot-btn">Forgot password?</button>
        </form>
      </div>
    </div>
  );
};

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

const compressImage = (file, maxWidth = 700, quality = 0.60) => new Promise((resolve, reject) => {
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    const scale = Math.min(1, maxWidth / img.width);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    resolve(canvas.toDataURL("image/jpeg", quality));
  };
  img.onerror = reject;
  img.src = url;
});

const ProductForm = ({ initial, onSave, onClose }) => {
  const sectionKeys = Object.keys(SECTIONS);
  const [f, setF] = useState(initial || { name: "", section: sectionKeys[0], category: "", price: 0, sale_price: "", currency: "JMD", description: "", image: "", stock: 100, featured: false, grades: [] });
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB"); return; }
    try {
      setF({ ...f, image: await compressImage(file) });
    } catch { toast.error("Could not process image"); }
  };
  const toggleGrade = (g) => setF({ ...f, grades: f.grades.includes(g) ? f.grades.filter(x => x !== g) : [...f.grades, g] });
  const submit = async (e) => {
    e.preventDefault();
    try {
      // Normalise sale_price: blank or 0 means "not on sale" → send null
      const payload = { ...f, sale_price: (f.sale_price !== "" && parseFloat(f.sale_price) > 0) ? parseFloat(f.sale_price) : null };
      if (initial?.id) { await api.put(`/admin/products/${initial.id}`, payload); toast.success("Product updated"); }
      else { await api.post("/admin/products", payload); toast.success("Product created"); }
      onSave();
    } catch { toast.error("Save failed"); }
  };
  const subOptions = SECTIONS[f.section] || [];
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="product-form-modal">
      <div className="bg-white border-[3px] border-black shadow-[6px_6px_0_#000] rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-['Fredoka'] font-bold">{initial ? "Edit" : "New"} Product</h2>
          <button onClick={onClose} className="p-2" data-testid="product-form-close"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input className="c4t-input" placeholder="Name" required value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="pf-name" />
          <div className="grid grid-cols-2 gap-3">
            <select className="c4t-input" value={f.section} onChange={e => setF({ ...f, section: e.target.value, category: "" })} data-testid="pf-section">
              {sectionKeys.map(s => <option key={s}>{s}</option>)}
            </select>
            <select className="c4t-input" value={f.category} onChange={e => {
              const cat = e.target.value;
              const autoGrades = f.grades.filter(x => cat === "Integrated Studies" ? INTEGRATED_GRADES.includes(x) : true);
              setF({ ...f, category: cat, grades: autoGrades });
            }} data-testid="pf-category" disabled={subOptions.length === 0}>
              <option value="">{subOptions.length === 0 ? "— No subcategory —" : "— Select subcategory —"}</option>
              {subOptions.map(c => <option key={c}>{c}</option>)}
            </select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-bold text-[#455A64] uppercase mb-1 h-5 flex items-center">Price (J$)</label>
                <input type="number" step="0.01" className="c4t-input" placeholder="0" required value={f.price} onChange={e => setF({ ...f, price: parseFloat(e.target.value) || 0 })} data-testid="pf-price" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1 h-5">
                  <label className="text-xs font-bold text-[#455A64] uppercase">On Sale</label>
                  <button type="button" aria-label="Toggle sale" onClick={() => { const on = f._sale_on ?? (Number(f.sale_price) > 0); const nx = !on; setF({ ...f, _sale_on: nx, sale_price: nx ? (Number(f.sale_price) > 0 ? f.sale_price : "") : "" }); }} className={`relative inline-block w-9 h-5 rounded-full transition-colors ${(f._sale_on ?? (Number(f.sale_price) > 0)) ? "bg-[#FF1F87]" : "bg-gray-300"}`}>
                    <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${(f._sale_on ?? (Number(f.sale_price) > 0)) ? "left-[18px]" : "left-0.5"}`}></span>
                  </button>
                </div>
                {(f._sale_on ?? (Number(f.sale_price) > 0)) ? (
                  <input type="number" step="0.01" className="c4t-input" placeholder="Sale price (J$)" value={f.sale_price} onChange={e => setF({ ...f, sale_price: e.target.value === "" ? "" : parseFloat(e.target.value) || 0 })} data-testid="pf-sale-price" />
                ) : (
                  <input type="text" disabled value="" className="c4t-input" style={{ background: "#f3f4f6" }} />
                )}
              </div>
              <div>
                <label className="text-xs font-bold text-[#455A64] uppercase mb-1 h-5 flex items-center">Stock</label>
                <input type="number" className="c4t-input" placeholder="0" value={f.stock} onChange={e => setF({ ...f, stock: parseInt(e.target.value) || 0 })} data-testid="pf-stock" />
              </div>
            </div>
          <label className="c4t-pill !justify-start cursor-pointer">
            <input type="checkbox" checked={f.featured} onChange={e => setF({ ...f, featured: e.target.checked })} data-testid="pf-featured" /> Featured (show on home page)
          </label>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="text-sm font-bold">Grades (select all that apply)</label>
              {f.category === "Integrated Studies" && (
                <span className="text-xs bg-[#FFC500] border border-black rounded-full px-2 py-0.5 font-semibold">
                  Locked to Grade 1–3 for Integrated Studies
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mt-1">
              {GRADES.map(g => {
                const isLocked = f.category === "Integrated Studies";
                const isActive = f.grades.includes(g);
                const isAllowed = !isLocked || INTEGRATED_GRADES.includes(g);
                return (
                  <button type="button" key={g}
                    onClick={() => isAllowed && toggleGrade(g)}
                    disabled={!isAllowed}
                    className={`c4t-pill !text-xs !py-1 !px-3 ${isActive ? "active" : ""} ${false ? "!border-[#FFC500]" : ""} ${!isAllowed ? "opacity-30 cursor-not-allowed" : ""}`}
                    data-testid={`pf-grade-${g.toLowerCase().replace(/\s+/g,'-')}`}>{g}</button>
                );
              })}
            </div>
          </div>
          <textarea className="c4t-input" rows={3} placeholder="Description" required value={f.description} onChange={e => setF({ ...f, description: e.target.value })} data-testid="pf-description" />
          <div>
            <label className="text-sm font-bold block mb-2">Image</label>
            {f.image
              ? (
                <div className="relative inline-block">
                  <img src={f.image} alt="Product" className="max-h-40 rounded-2xl border-2 border-black" />
                  <button
                    type="button"
                    onClick={() => setF({ ...f, image: "" })}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-[#FF1F87] text-white rounded-full border-2 border-black text-xs font-bold flex items-center justify-center"
                    title="Remove image"
                  >x</button>
                </div>
              )
              : (
                <label className="flex items-center gap-2 c4t-pill cursor-pointer w-fit">
                  <ImageIcon className="w-4 h-4" /> Upload Image
                  <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="pf-image-upload" />
                </label>
              )
            }
          </div>
          <button type="submit" className="c4t-btn c4t-btn-pink w-full" data-testid="pf-submit">Save Product</button>
        </form>
      </div>
    </div>
  );
};

const SkeletonCard = () => (
  <div className="c4t-card !p-3 animate-pulse">
    <div className="aspect-square bg-gray-100 rounded-2xl" />
    <div className="h-5 bg-gray-100 rounded-xl mt-2 w-3/4" />
    <div className="h-3 bg-gray-100 rounded mt-2 w-1/2" />
    <div className="h-3 bg-gray-100 rounded mt-1 w-2/3" />
    <div className="h-4 bg-gray-100 rounded mt-2 w-1/3" />
    <div className="flex gap-2 mt-3">
      <div className="flex-1 h-8 bg-gray-100 rounded-xl" />
      <div className="w-8 h-8 bg-gray-100 rounded-xl" />
    </div>
  </div>
);

const ProductsTab = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [catalog, setCatalog] = useState({ sections: SECTIONS, grades: GRADES });
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [fSection, setFSection] = useState("");
  const [fGrade, setFGrade] = useState("");
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const load = () => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), limit: "10" });
    if (fSection) params.set("section", fSection);
    if (fGrade) params.set("grade", fGrade);
    if (q) params.set("q", q);
    api.get("/admin/products?" + params.toString()).then(r => {
      const d = r.data;
      if (Array.isArray(d)) { setProducts(d); setTotal(d.length); setPages(1); }
      else { setProducts(d.items || []); setTotal(d.total || 0); setPages(d.pages || 1); }
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [page, fSection, fGrade, q]);
  useEffect(() => { api.get("/catalog").then(r => setCatalog(r.data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(() => { setPage(1); setQ(qInput.trim()); }, 400); return () => clearTimeout(t); }, [qInput]);
  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted"); load();
  };
  return (
    <div data-testid="admin-products-tab">
      <div className="flex justify-between mb-4">
        <h2 className="text-2xl font-['Fredoka'] font-bold">Products {loading ? "" : `(${total})`}</h2>
        <div className="flex gap-2">
          <button onClick={async () => { try { await api.post("/admin/products/rewatermark-all"); toast.success("Re-watermarking all products..."); } catch { toast.error("Failed"); } }} className="c4t-pill !py-1.5 !px-3 !text-sm flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Re-watermark All</button>
          <button onClick={() => { setEditing(null); setShowForm(true); }} className="c4t-btn c4t-btn-green" data-testid="add-product-btn"><Plus className="w-4 h-4" /> New</button>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <input className="c4t-input !py-2 !px-4 !text-sm sm:flex-1 sm:!w-auto" placeholder="Search by name..." value={qInput} onChange={e => setQInput(e.target.value)} data-testid="admin-product-search" />
        <select className="c4t-input !py-2 !px-4 !text-sm sm:!w-48" value={fSection} onChange={e => { setPage(1); setFSection(e.target.value); }} data-testid="admin-product-section-filter">
          <option value="">All sections</option>
          {(Object.keys(catalog.sections || SECTIONS)).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="c4t-input !py-2 !px-4 !text-sm sm:!w-40" value={fGrade} onChange={e => { setPage(1); setFGrade(e.target.value); }} data-testid="admin-product-grade-filter">
          <option value="">All grades</option>
          {(catalog.grades || GRADES).map(g => <option key={g} value={g}>{g}</option>)}
        </select>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          [...Array(6)].map((_, i) => <SkeletonCard key={i} />)
        ) : products.map(p => (
          <div key={p.id} className="c4t-card !p-3" data-testid={`admin-product-${p.id}`}>
            <div className="aspect-square overflow-hidden rounded-2xl bg-white border-2 border-black/30">
              {p.image ? (
                <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#073B4C]/30 text-xs">{loading || saving[key] ? <div className="w-8 h-8 rounded-full border-4 border-[#FF1F87]/30 border-t-[#FF1F87] animate-spin" /> : "No image"}</div>
              )}
            </div>
            <h3 className="font-['Fredoka'] font-bold mt-2">{p.name}</h3>
            <p className="c4t-desc text-xs">{p.section}{p.category ? ` › ${p.category}` : ""}</p>
            <p className="c4t-desc text-xs">Stock: {p.stock} {p.grades?.length ? `• ${p.grades.join(", ")}` : ""}</p>
            <p className="text-[#FF1F87] font-bold">J${Math.round(p.price).toLocaleString()} JMD</p>
            <div className="flex gap-2 mt-2">
              <button onClick={() => { setEditing(p); setShowForm(true); }} className="c4t-pill !py-1 !px-3 flex-1" data-testid={`edit-product-${p.id}`}><Pencil className="w-3 h-3" /> Edit</button>
              <button onClick={() => del(p.id)} className="c4t-pill !py-1 !px-3 !bg-[#FF1F87] !text-white" data-testid={`delete-product-${p.id}`}><Trash2 className="w-3 h-3" /></button>
            </div>
          </div>
        ))}
      </div>
      {!loading && pages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-6">
          <button onClick={() => { setPage(x => Math.max(1, x - 1)); document.querySelector("main")?.scrollTo({ top: 0, behavior: "smooth" }); }} disabled={page <= 1} className="c4t-pill !py-1.5 !px-4 disabled:opacity-40" data-testid="admin-products-prev">Prev</button>
          <span className="text-sm font-bold">Page {page} of {pages}</span>
          <button onClick={() => { setPage(x => Math.min(pages, x + 1)); document.querySelector("main")?.scrollTo({ top: 0, behavior: "smooth" }); }} disabled={page >= pages} className="c4t-pill !py-1.5 !px-4 disabled:opacity-40" data-testid="admin-products-next">Next</button>
        </div>
      )}
      {showForm && <ProductForm initial={editing} onSave={() => { setShowForm(false); load(); }} onClose={() => setShowForm(false)} catalog={catalog} />}
    </div>
  );
};


// ── Sections & Grades Management Tab ──
const SectionsTab = () => {
  const [sections, setSections] = useState(null);
  const [grades, setGrades] = useState(null);
  const [usage, setUsage] = useState({ sections: {}, categories: {}, grades: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newSectionName, setNewSectionName] = useState("");
  const [newItems, setNewItems] = useState({});
  const [newGrade, setNewGrade] = useState("");

  const loadUsage = () =>
    api.get("/admin/catalog/usage").then(r => setUsage(r.data)).catch(() => {});

  useEffect(() => {
    Promise.all([
      api.get("/catalog"),
      api.get("/admin/catalog/usage"),
    ]).then(([cat, use]) => {
      setSections({ ...cat.data.sections });
      setGrades([...cat.data.grades]);
      setUsage(use.data);
    }).finally(() => setLoading(false));
  }, []);

  // Auto-saves the given sections + grades immediately
  const persist = async (nextSections, nextGrades) => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { catalog_sections: nextSections, catalog_grades: nextGrades });
      toast.success("Saved!");
      loadUsage(); // refresh counts after any structural change
    } catch { toast.error("Save failed — check your connection"); }
    finally { setSaving(false); }
  };

  // Section group operations
  const addSection = () => {
    const name = newSectionName.trim();
    if (!name || sections[name] !== undefined) return;
    const next = { ...sections, [name]: [] };
    setSections(next);
    setNewSectionName("");
    persist(next, grades);
  };
  const removeSection = (key) => {
    const count = usage.sections[key] || 0;
    if (count > 0) {
      toast.error(`Cannot delete "${key}" — ${count} product${count === 1 ? "" : "s"} are listed under this section. Reassign or delete those products first.`);
      return;
    }
    if (!window.confirm(`Delete section "${key}" and all its subcategories?`)) return;
    const next = { ...sections };
    delete next[key];
    setSections(next);
    persist(next, grades);
  };
  const renameSection = (oldKey, newKey) => {
    newKey = newKey.trim();
    if (!newKey || newKey === oldKey) return;
    const next = {};
    for (const [k, v] of Object.entries(sections)) {
      next[k === oldKey ? newKey : k] = v;
    }
    setSections(next);
    persist(next, grades);
  };

  // Subcategory operations
  const addSubcategory = (sectionKey) => {
    const item = (newItems[sectionKey] || "").trim();
    if (!item) return;
    if (sections[sectionKey].includes(item)) { toast.error("Already exists"); return; }
    const next = { ...sections, [sectionKey]: [...sections[sectionKey], item] };
    setSections(next);
    setNewItems({ ...newItems, [sectionKey]: "" });
    persist(next, grades);
  };
  const removeSubcategory = (sectionKey, item) => {
    const key = `${sectionKey}||${item}`;
    const count = usage.categories[key] || 0;
    if (count > 0) {
      toast.error(`Cannot delete "${item}" — ${count} product${count === 1 ? "" : "s"} use this subcategory. Reassign or delete those products first.`);
      return;
    }
    const next = { ...sections, [sectionKey]: sections[sectionKey].filter(x => x !== item) };
    setSections(next);
    persist(next, grades);
  };

  // Grade operations
  const addGrade = () => {
    const g = newGrade.trim();
    if (!g || grades.includes(g)) return;
    const next = [...grades, g];
    setGrades(next);
    setNewGrade("");
    persist(sections, next);
  };
  const removeGrade = (g) => {
    const count = usage.grades[g] || 0;
    if (count > 0) {
      toast.error(`Cannot delete "${g}" — ${count} product${count === 1 ? "" : "s"} are tagged with this grade. Reassign or delete those products first.`);
      return;
    }
    const next = grades.filter(x => x !== g);
    setGrades(next);
    persist(sections, next);
  };
  const moveGrade = (idx, dir) => {
    const next = [...grades];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setGrades(next);
    persist(sections, next);
  };

  if (loading) return <div className="animate-pulse space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-gray-100 rounded-2xl" />)}</div>;
  if (!sections) return null;

  return (
    <div data-testid="admin-sections-tab">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-['Fredoka'] font-bold">Sections & Grades</h2>
        <span className="text-sm text-[#455A64] italic">{saving ? "Saving…" : "Changes save automatically"}</span>
      </div>

      {/* Section Groups */}
      <div className="space-y-4 mb-8">
        <h3 className="text-lg font-['Fredoka'] font-bold text-[#073B4C]">Shop Sections</h3>
        <p className="text-sm text-[#455A64]">These appear as filter tabs in the shop. Each section can have subcategories.</p>

        {Object.entries(sections).map(([key, items]) => (
          <div key={key} className="c4t-card !p-4">
            <div className="flex items-center gap-2 mb-3">
              <input
                className="c4t-input flex-1 !text-base font-semibold"
                defaultValue={key}
                onBlur={e => renameSection(key, e.target.value)}
              />
              <button onClick={() => removeSection(key)} className="c4t-pill !py-1 !px-3 !bg-red-50 !text-red-500 !border-red-200 shrink-0">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Subcategories */}
            <div className="flex flex-wrap gap-2 mb-3">
              {items.map(item => (
                <span key={item} className="c4t-pill !text-xs !py-1 active flex items-center gap-1">
                  {item}
                  <button onClick={() => removeSubcategory(key, item)} className="ml-1 hover:text-red-400">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {items.length === 0 && <span className="text-xs text-[#455A64] italic">No subcategories</span>}
            </div>

            {/* Add subcategory */}
            <div className="flex gap-2">
              <input
                className="c4t-input flex-1 !text-sm"
                placeholder="Add subcategory…"
                value={newItems[key] || ""}
                onChange={e => setNewItems({ ...newItems, [key]: e.target.value })}
                onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addSubcategory(key))}
              />
              <button onClick={() => addSubcategory(key)} className="c4t-btn c4t-btn-green !text-xs !px-4">
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
          </div>
        ))}

        {/* Add new section group */}
        <div className="flex gap-2">
          <input
            className="c4t-input flex-1"
            placeholder="New section name (e.g. Holiday Decor)…"
            value={newSectionName}
            onChange={e => setNewSectionName(e.target.value)}
            onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addSection())}
          />
          <button onClick={addSection} className="c4t-btn c4t-btn-pink">
            <Plus className="w-4 h-4" /> Add Section
          </button>
        </div>
      </div>

      {/* Grades */}
      <div>
        <h3 className="text-lg font-['Fredoka'] font-bold text-[#073B4C] mb-1">Shop by Grade</h3>
        <p className="text-sm text-[#455A64] mb-3">These appear as grade filters in the shop and in the product form.</p>

        <div className="flex flex-col gap-2 max-w-sm mb-4">
          {grades.map((g, idx) => (
            <div key={g} className="flex items-center gap-2">
              <span className="flex-1 c4t-pill !justify-start">{g}</span>
              <button onClick={() => moveGrade(idx, -1)} disabled={idx === 0} className="c4t-pill !py-1 !px-3 disabled:opacity-30">↑</button>
              <button onClick={() => moveGrade(idx, 1)} disabled={idx === grades.length - 1} className="c4t-pill !py-1 !px-3 disabled:opacity-30">↓</button>
              <button onClick={() => removeGrade(g)} className="c4t-pill !py-1 !px-3 !bg-red-50 !text-red-500 !border-red-200">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>

        <div className="flex gap-2 max-w-sm">
          <input
            className="c4t-input flex-1"
            placeholder="New grade (e.g. Grade 7)…"
            value={newGrade}
            onChange={e => setNewGrade(e.target.value)}
            onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addGrade())}
          />
          <button onClick={addGrade} className="c4t-btn c4t-btn-pink">
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
      </div>
    </div>
  );
};

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const OrdersTab = () => {
  const [orders, setOrders] = useState([]);
  const [oPage, setOPage] = useState(1);
  const [viewProof, setViewProof] = useState(null);
  const [calView, setCalView] = useState(false);
  const now = new Date();
  const [filterYear, setFilterYear] = useState(now.getFullYear());
  const [filterMonth, setFilterMonth] = useState(null); // null = show all months

  const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true); api.get("/admin/orders").then(r => setOrders(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const fmtOrder = (o, val) => {
    // JMD-first orders: totals_currency="JMD" means val is already in JMD
    if (o.totals_currency === "JMD") {
      return `J$${Math.round(val).toLocaleString("en-US")} JMD`;
    }
    // Legacy USD orders: val is USD, convert using stored exchange_rate
    if ((o.currency || "USD") === "JMD" && o.exchange_rate > 1) {
      return `J$${Math.round(val * o.exchange_rate).toLocaleString("en-US")} JMD`;
    }
    return `$${Number(val).toFixed(2)} USD`;
  };

  const updateStatus = async (id, status) => {
    await api.put(`/admin/orders/${id}/status`, { status });
    toast.success("Status updated"); load();
  };
  const deleteOrder = async (o) => {
    if (!window.confirm(`Delete order #${o.id.slice(0,8)} for ${o.customer_name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/orders/${o.id}`);
      toast.success("Order deleted");
      load();
    } catch { toast.error("Failed to delete order"); }
  };

  // Years available from order data
  const years = [...new Set(orders.map(o => new Date(o.created_at).getFullYear()))].sort((a,b) => b-a);
  const allYears = [...new Set([...years, now.getFullYear()])].sort((a,b) => b-a);

  // Count orders per month for calendar view
  const monthCounts = Array(12).fill(0);
  orders.forEach(o => {
    const d = new Date(o.created_at);
    if (filterYear === "all" || d.getFullYear() === filterYear) monthCounts[d.getMonth()]++;
  });

  // Filter orders for list view
  const filteredOrders = orders.filter(o => {
    const d = new Date(o.created_at);
    if (filterYear !== "all" && d.getFullYear() !== filterYear) return false;
    if (filterMonth !== null && d.getMonth() !== filterMonth) return false;
    return true;
  });

  const statusColor = { pending: "#FFC500", processing: "#00A6E0", shipped: "#00E0AA", completed: "#4CAF50", cancelled: "#FF1F87" };

  return (
    <div data-testid="admin-orders-tab">
      {/* Header + controls */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-2xl font-['Fredoka'] font-bold">Orders {loading ? "" : `(${orders.length})`}</h2>
        <div className="flex flex-wrap items-center gap-2">
          <select value={filterYear} onChange={e => { setFilterYear(e.target.value === "all" ? "all" : Number(e.target.value)); setFilterMonth(null); }}
            className="c4t-input !py-1 !px-3 !text-sm">
            <option value="all">All time</option>
            {allYears.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={() => setCalView(!calView)}
            className={`c4t-pill !py-1 !px-3 !text-sm ${calView ? "!bg-[#FF1F87] !text-white" : ""}`}>
            {calView ? "List View" : "Calendar View"}
          </button>
        </div>
      </div>

      {/* Calendar month grid */}
      {calView && (
        <div className="mb-6">
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {MONTHS.map((name, idx) => {
              const count = monthCounts[idx];
              const isSelected = filterMonth === idx;
              const isFuture = filterYear !== "all" && filterYear === now.getFullYear() && idx > now.getMonth();
              return (
                <button key={idx}
                  onClick={() => { setFilterMonth(isSelected ? null : idx); setCalView(false); }}
                  disabled={isFuture}
                  className={`c4t-card !p-3 text-center transition-all ${isSelected ? "border-[#FF1F87] !border-[3px]" : ""} ${isFuture ? "opacity-40 cursor-not-allowed" : "hover:border-[#FF1F87] cursor-pointer"}`}>
                  <p className="font-['Fredoka'] font-bold text-sm">{name}</p>
                  <p className={`text-2xl font-black mt-1 ${count > 0 ? "text-[#FF1F87]" : "text-gray-300"}`}>{count}</p>
                  <p className="text-xs c4t-desc">{count === 1 ? "order" : "orders"}</p>
                </button>
              );
            })}
          </div>
          <p className="text-sm c4t-desc mt-2 text-center">Click a month to filter orders</p>
        </div>
      )}

      {/* Active filter chip */}
      {filterMonth !== null && (
        <div className="flex items-center gap-2 mb-4">
          <span className="c4t-pill !bg-[#FF1F87] !text-white !text-sm">
            {MONTHS[filterMonth]} {filterYear} — {filteredOrders.length} order{filteredOrders.length !== 1 ? "s" : ""}
          </span>
          <button onClick={() => setFilterMonth(null)} className="c4t-pill !py-1 !px-3 !text-sm">✕ Clear</button>
        </div>
      )}

      {/* Orders list */}
      <div className="space-y-4">
        {loading ? (
          [...Array(4)].map((_, i) => (
            <div key={i} className="c4t-card animate-pulse">
              <div className="flex flex-wrap justify-between gap-3">
                <div className="space-y-2 flex-1">
                  <div className="h-5 bg-gray-100 rounded w-1/3" />
                  <div className="h-3 bg-gray-100 rounded w-1/2" />
                  <div className="h-3 bg-gray-100 rounded w-2/3" />
                  <div className="h-3 bg-gray-100 rounded w-1/4" />
                </div>
                <div className="space-y-2 text-right">
                  <div className="h-7 bg-gray-100 rounded w-28 ml-auto" />
                  <div className="h-3 bg-gray-100 rounded w-20 ml-auto" />
                  <div className="h-8 bg-gray-100 rounded w-28 ml-auto" />
                </div>
              </div>
            </div>
          ))
        ) : filteredOrders.length === 0 ? (
          <p className="c4t-desc">No orders {filterMonth !== null ? `in ${MONTHS[filterMonth]} ${filterYear === "all" ? "" : filterYear}` : filterYear === "all" ? "yet" : `in ${filterYear}`}.</p>
        ) : (
          filteredOrders.slice((oPage-1)*5, oPage*5).map(o => (
            <div key={o.id} className="c4t-card" data-testid={`admin-order-${o.id}`}>
            <div className="flex flex-wrap justify-between gap-3">
              <div>
                <p className="font-['Fredoka'] font-bold text-lg">{o.customer_name} <span className="text-sm c4t-desc">#{o.id.slice(0, 8)}</span></p>
                <p className="text-sm c4t-desc">{o.email} • {o.phone} • {o.country}</p>
                <p className="text-sm c4t-desc">{o.shipping_address}</p>
                <p className="text-xs c4t-desc mt-1">{new Date(o.created_at).toLocaleDateString("en-US", {year:"numeric",month:"short",day:"numeric"})}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-[#FF1F87]">{fmtOrder(o, o.total)}</p>
                <p className="text-xs c4t-desc">{o.payment_method.replace("_", " ")} • {o.shipping_option}</p>
                {o.payment_status === "paid" && o.payment_method === "wipay" ? (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full" style={{background:"#dcfce7",color:"#15803d"}}>
                    Paid by Card (WiPay)
                  </span>
                ) : o.payment_status === "paid" && o.payment_method === "paypal" ? (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full" style={{background:"#dbeafe",color:"#1d4ed8"}}>
                    Paid via PayPal
                  </span>
                ) : o.payment_status === "paid" ? (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full" style={{background:"#dcfce7",color:"#15803d"}}>
                    Paid
                  </span>
                ) : o.payment_method === "wipay" && o.payment_status === "pending" ? (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full" style={{background:"#fff7ed",color:"#c2410c"}}>
                    WiPay – Awaiting Payment
                  </span>
                ) : o.payment_method === "wipay" && o.payment_status === "failed" ? (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full" style={{background:"#fee2e2",color:"#b91c1c"}}>
                    Card Payment Failed
                  </span>
                ) : o.payment_method === "bank_transfer" ? (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full" style={["processing","shipped","completed"].includes(String(o.status||"").toLowerCase()) ? {background:"#dcfce7",color:"#166534"} : {background:"#fef9c3",color:"#92400e"}}>{["processing","shipped","completed"].includes(String(o.status||"").toLowerCase()) ? "Bank Transfer – Paid" : "Bank Transfer – Verify Payment"}</span>
                ) : null}
                <p className="text-xs c4t-desc">Subtotal: {fmtOrder(o, o.subtotal)} + Shipping: {fmtOrder(o, o.shipping_cost || 0)}{o.discount > 0 ? ` − Discount: ${fmtOrder(o, o.discount)}` : ""}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {o.voucher_discount > 0 && (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full" style={{background:"#fef9c3",color:"#92400e"}}>
                      Voucher ({o.voucher_code}): −{fmtOrder(o, o.voucher_discount)}
                    </span>
                  )}
                  {o.store_credit_used > 0 && (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full" style={{background:"#ede9fe",color:"#5b21b6"}}>
                      Store Credit: −{fmtOrder(o, o.store_credit_used)}
                    </span>
                  )}
                </div>
                <select value={o.status} onChange={e => updateStatus(o.id, e.target.value)} className="c4t-input !py-1 !px-2 !text-sm mt-1" data-testid={`order-status-${o.id}`}>
                  <option>pending</option><option>processing</option><option>shipped</option><option>completed</option><option>cancelled</option>
                </select>
              </div>
            </div>
            <details className="mt-3">
              <summary className="cursor-pointer font-semibold">Items ({o.items.length})</summary>
              <ul className="ml-4 mt-1 text-sm c4t-desc">
                {o.items.map((i, idx) => <li key={idx}>{i.quantity} × {i.name} — {fmtOrder(o, i.price * i.quantity)}</li>)}
              </ul>
            </details>
            {o.notes && <p className="mt-2 text-sm italic c4t-desc">Note: {o.notes}</p>}

            {/* Payment & Delivery Details — sub-options and custom required fields */}
            {(o.shipping_sub_option || (o.shipping_fields && Object.keys(o.shipping_fields).length > 0)) && (
              <div className="mt-3 p-3 rounded-xl border border-[#e5e7eb] bg-[#f9fafb]">
                <p className="text-xs font-bold uppercase tracking-wide text-[#455A64] mb-2">Delivery Details</p>
                {o.shipping_sub_option && (
                  <p className="text-sm mb-1">
                    <span className="font-semibold">{o.shipping_option}:</span>{" "}
                    {o.shipping_sub_option}
                  </p>
                )}
                {o.shipping_fields && Object.entries(o.shipping_fields).map(([key, val]) => val ? (
                  <p key={key} className="text-sm">
                    <span className="font-semibold">{key}:</span> {val}
                  </p>
                ) : null)}
              </div>
            )}

            <div className="flex flex-wrap gap-2 mt-3">
              {o.has_payment_proof && (
                <button onClick={async () => { try { const r = await api.get(`/admin/orders/${o.id}/payment-proof`); setViewProof(r.data.payment_proof); } catch (e) {} }} className="c4t-pill !py-1 !px-3" data-testid={`view-proof-${o.id}`}>
                  <ImageIcon className="w-3 h-3" /> View Payment Proof
                </button>
              )}
              <button onClick={() => deleteOrder(o)} className="c4t-pill !py-1 !px-3 !bg-red-50 !text-red-600 hover:!bg-red-100" data-testid={`delete-order-${o.id}`}>
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            </div>
          </div>
          ))
        )}
      {!loading && Math.ceil(filteredOrders.length/5) > 1 && (
        <div className="flex items-center justify-center gap-3 mt-6">
          <button onClick={() => { setOPage(x => Math.max(1, x - 1)); document.querySelector("main")?.scrollTo({ top: 0, behavior: "smooth" }); }} disabled={oPage <= 1} className="c4t-pill !py-1.5 !px-4 disabled:opacity-40">Prev</button>
          <span className="text-sm font-bold">Page {oPage} of {Math.ceil(filteredOrders.length/5)}</span>
          <button onClick={() => { setOPage(x => Math.min(Math.ceil(filteredOrders.length/5), x + 1)); document.querySelector("main")?.scrollTo({ top: 0, behavior: "smooth" }); }} disabled={oPage >= Math.ceil(filteredOrders.length/5)} className="c4t-pill !py-1.5 !px-4 disabled:opacity-40">Next</button>
        </div>
      )}
      </div>
      {viewProof && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setViewProof(null)}>
          <img src={viewProof} alt="proof" className="max-h-[90vh] max-w-full rounded-2xl border-[3px] border-white" data-testid="proof-modal" />
        </div>
      )}
    </div>
  );
};

const UsersTab = () => {
  const { exchangeRate } = useSettings();
  const [users, setUsers] = useState([]);
  const [uPage, setUPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [creditModal, setCreditModal] = useState(null);
  const [creditAmt, setCreditAmt] = useState("");
  const [creditCur, setCreditCur] = useState("USD");
  const [creditBusy, setCreditBusy] = useState(false);
  useEffect(() => { api.get("/admin/users").then(r => setUsers(r.data)).finally(() => setLoading(false)); }, []);

  const addCredit = async () => {
    const amt = parseFloat(creditAmt);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setCreditBusy(true);
    try {
      const res = await api.post(`/admin/users/${creditModal.user_id}/credit`, { amount: amt, currency: creditCur, action: "add" });
      const symb = creditCur === "JMD" ? "J$" : "$";
      toast.success(`${symb}${amt.toFixed(2)} ${creditCur} credit added to ${creditModal.name}`);
      setCreditModal(null); setCreditAmt(""); setCreditCur("USD");
      setUsers(prev => prev.map(u => u.user_id === creditModal.user_id
        ? { ...u, store_credit_amount: res.data.store_credit_amount, store_credit_currency: res.data.store_credit_currency }
        : u));
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add credit"); }
    finally { setCreditBusy(false); }
  };

  const clearCredit = async (userId) => {
    if (!window.confirm("Remove all store credit for this customer?")) return;
    try {
      await api.delete(`/admin/users/${userId}/credit`);
      toast.success("Store credit cleared");
      setUsers(prev => prev.map(u => u.user_id === userId
        ? { ...u, store_credit_amount: 0, store_credit_currency: "USD" }
        : u));
    } catch { toast.error("Failed to clear credit"); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.name} (${u.email})? This anonymises their orders and cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success(`${u.name} deleted`);
      setUsers(prev => prev.filter(x => x.user_id !== u.user_id));
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete customer"); }
  };

  return (
    <div data-testid="admin-users-tab">
      <h2 className="text-2xl font-['Fredoka'] font-bold mb-4">Customers {loading ? "" : `(${users.length})`}</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {loading ? (
          [...Array(6)].map((_, i) => (
            <div key={i} className="c4t-card flex items-center gap-3 animate-pulse">
              <div className="w-12 h-12 rounded-full bg-gray-100 shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-100 rounded w-3/4" />
                <div className="h-3 bg-gray-100 rounded w-full" />
              </div>
            </div>
          ))
        ) : users.length === 0 ? (
          <p className="c4t-desc">No customers yet.</p>
        ) : (
          users.slice((uPage-1)*10, uPage*10).map(u => (
            <div key={u.user_id} className="c4t-card flex items-start gap-3">
              {u.picture ? <img src={u.picture} alt="" className="w-12 h-12 rounded-full border-2 border-black shrink-0" /> : <div className="w-12 h-12 rounded-full bg-gray-100 border-2 border-black shrink-0 flex items-center justify-center font-bold text-lg">{u.name?.[0]}</div>}
              <div className="flex-1 min-w-0">
                <p className="font-bold truncate">{u.name}</p>
                <p className="text-xs c4t-desc truncate">{u.email}</p>
                {(u.store_credit_amount || 0) > 0 && (
                  <p className="text-xs font-bold text-[#00A6E0] mt-1 flex items-center gap-1">
                    <Wallet className="w-3 h-3" />
                    {u.store_credit_currency === "JMD" ? "J$" : "$"}{Number(u.store_credit_amount).toFixed(2)} {u.store_credit_currency || "USD"} credit
                  </p>
                )}
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <button onClick={() => { setCreditModal(u); setCreditAmt(""); }} className="c4t-pill !py-1 !px-2 !text-xs"><CreditCard className="w-3 h-3" /> Add Credit</button>
                  {(u.store_credit_amount || 0) > 0 && <button onClick={() => clearCredit(u.user_id)} className="c4t-pill !py-1 !px-2 !text-xs !bg-[#FF1F87] !text-white"><Trash2 className="w-3 h-3" /> Clear</button>}
                  <button onClick={() => deleteUser(u)} className="c4t-pill !py-1 !px-2 !text-xs !bg-red-600 !text-white !border-red-800" title="Delete customer account"><Trash2 className="w-3 h-3" /> Delete</button>
                </div>
              </div>
            </div>
          ))
        )}
      {!loading && Math.ceil(users.length/10) > 1 && (
        <div className="flex items-center justify-center gap-3 mt-6">
          <button onClick={() => { setUPage(x => Math.max(1, x - 1)); document.querySelector("main")?.scrollTo({ top: 0, behavior: "smooth" }); }} disabled={uPage <= 1} className="c4t-pill !py-1.5 !px-4 disabled:opacity-40">Prev</button>
          <span className="text-sm font-bold">Page {uPage} of {Math.ceil(users.length/10)}</span>
          <button onClick={() => { setUPage(x => Math.min(Math.ceil(users.length/10), x + 1)); document.querySelector("main")?.scrollTo({ top: 0, behavior: "smooth" }); }} disabled={uPage >= Math.ceil(users.length/10)} className="c4t-pill !py-1.5 !px-4 disabled:opacity-40">Next</button>
        </div>
      )}
      </div>

      {/* Add Credit Modal */}
      {creditModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setCreditModal(null)}>
          <div className="bg-white border-[3px] border-black rounded-3xl shadow-[6px_6px_0_#000] p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <h3 className="font-['Fredoka'] font-bold text-xl mb-1">Add Store Credit</h3>
            <p className="c4t-desc text-sm mb-4">Adding credit to <strong>{creditModal.name}</strong>. Auto-applied at their next checkout.</p>
            {(creditModal.store_credit_amount || 0) > 0 && (
              <p className="text-sm font-semibold text-[#00A6E0] mb-3">
                Current balance: {creditModal.store_credit_currency === "JMD" ? "J$" : "$"}{Number(creditModal.store_credit_amount).toFixed(2)} {creditModal.store_credit_currency || "USD"}
              </p>
            )}
            <div className="flex items-center gap-2 mb-1">
              <label className="text-sm font-bold">Amount</label>
              <button type="button" onClick={() => setCreditCur("USD")} className={`c4t-pill !py-0.5 !px-2 !text-xs ${creditCur==="USD" ? "!bg-black !text-white" : ""}`}>USD</button>
              <button type="button" onClick={() => setCreditCur("JMD")} className={`c4t-pill !py-0.5 !px-2 !text-xs ${creditCur==="JMD" ? "!bg-black !text-white" : ""}`}>JMD</button>
            </div>
            <input type="number" step="0.01" min="0.01" autoFocus className="c4t-input mt-1 mb-1" placeholder={creditCur === "JMD" ? "8000.00" : "50.00"} value={creditAmt} onChange={e => setCreditAmt(e.target.value)} onKeyDown={e => e.key === "Enter" && addCredit()} />
            <div className="mb-3" />
            <div className="flex gap-3">
              <button onClick={addCredit} disabled={creditBusy} className="c4t-btn c4t-btn-green flex-1">{creditBusy ? "Adding..." : "Add Credit"}</button>
              <button onClick={() => setCreditModal(null)} className="c4t-pill flex-1">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MessagesTab = () => {
  const [msgs, setMsgs] = useState([]);
  const [mPage, setMPage] = useState(1);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/admin/contact-messages").then(r => setMsgs(r.data)).finally(() => setLoading(false)); }, []);
  return (
    <div data-testid="admin-messages-tab">
      <h2 className="text-2xl font-['Fredoka'] font-bold mb-4">Contact Messages {loading ? "" : `(${msgs.length})`}</h2>
      <div className="space-y-3">
        {loading ? (
          [...Array(3)].map((_, i) => (
            <div key={i} className="c4t-card animate-pulse space-y-2">
              <div className="h-4 bg-gray-100 rounded w-1/3" />
              <div className="h-3 bg-gray-100 rounded w-full" />
              <div className="h-3 bg-gray-100 rounded w-4/5" />
              <div className="h-3 bg-gray-100 rounded w-1/4 mt-2" />
            </div>
          ))
        ) : msgs.length === 0 ? (
          <p className="c4t-desc">No messages yet.</p>
        ) : (
          msgs.slice((mPage-1)*10, mPage*10).map(m => (
            <div key={m.id} className="c4t-card">
              <p className="font-bold">{m.name} <span className="text-sm c4t-desc font-normal">— {m.email}</span></p>
              <p className="c4t-desc mt-1">{m.message}</p>
              <p className="text-xs c4t-desc mt-2">{new Date(m.created_at).toLocaleString()}</p>
            </div>
          ))
        )}
      {!loading && Math.ceil(msgs.length/10) > 1 && (
        <div className="flex items-center justify-center gap-3 mt-6">
          <button onClick={() => { setMPage(x => Math.max(1, x - 1)); document.querySelector("main")?.scrollTo({ top: 0, behavior: "smooth" }); }} disabled={mPage <= 1} className="c4t-pill !py-1.5 !px-4 disabled:opacity-40">Prev</button>
          <span className="text-sm font-bold">Page {mPage} of {Math.ceil(msgs.length/10)}</span>
          <button onClick={() => { setMPage(x => Math.min(Math.ceil(msgs.length/10), x + 1)); document.querySelector("main")?.scrollTo({ top: 0, behavior: "smooth" }); }} disabled={mPage >= Math.ceil(msgs.length/10)} className="c4t-pill !py-1.5 !px-4 disabled:opacity-40">Next</button>
        </div>
      )}
      </div>
    </div>
  );
};

const CategoryImagesTab = () => {
  const [images, setImages] = useState({});
  const [saving, setSaving] = useState({});
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState(null);
  useEffect(() => {
    Promise.all([
      api.get("/admin/category-images"),
      api.get("/catalog"),
    ]).then(([imgRes, catRes]) => {
      setImages(imgRes.data || {});
      setCatalog(catRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);
  // Stable kind mapping — preserves existing image keys (e.g. subject:Mathematics)
  const SECTION_KIND_MAP = {
    "Charts by Subject": "subject",
    "Class Decor": "decor",
    "Teacher Essentials": "essentials",
    "Graduation & Teacher Appreciation": "graduation",
  };
  const getKind = (label) =>
    SECTION_KIND_MAP[label] || label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const categoryGroups = catalog ? [
    ...Object.entries(catalog.sections).map(([label, items]) => ({
      kind: getKind(label),
      label,
      items,
    })),
    { kind: "grade", label: "Shop by Grade", items: catalog.grades },
  ] : CATEGORY_KINDS;
  const upload = async (key, file) => {
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB"); return; }
    try {
      const data = await compressImage(file);
      setImages(prev => ({ ...prev, [key]: data }));
      setSaving(prev => ({ ...prev, [key]: true }));
      await api.put(`/admin/category-images/${encodeURIComponent(key)}`, { image: data });
      setSaving(prev => ({ ...prev, [key]: false }));
      toast.success("Image saved!");
    } catch (err) {
      setSaving(prev => ({ ...prev, [key]: false }));
      toast.error("Failed to save image. Try a smaller file.");
    }
  };
  const clear = async (key) => {
    setImages(prev => { const n = { ...prev }; delete n[key]; return n; });
    try { await api.delete(`/admin/category-images/${encodeURIComponent(key)}`); toast.success("Image removed"); }
    catch { toast.error("Failed to remove image"); }
  };
  if (loading) return (
    <div data-testid="admin-category-images-tab">
      <h2 className="text-2xl font-['Fredoka'] font-bold mb-6">Category Images</h2>
      {[1,2].map(g => (
        <div key={g} className="mb-8">
          <div className="h-6 bg-gray-100 rounded w-40 mb-3 animate-pulse" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="c4t-card !p-3 animate-pulse">
                <div className="aspect-square bg-gray-100 rounded-2xl" />
                <div className="h-4 bg-gray-100 rounded mt-2 mx-auto w-2/3" />
                <div className="h-8 bg-gray-100 rounded-xl mt-2" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
  return (
    <div data-testid="admin-category-images-tab">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-['Fredoka'] font-bold">Category Images</h2>
        <span className="text-sm text-[#455A64] italic">Images save automatically on upload</span>
      </div>
      <p className="c4t-desc mb-6">Upload an image for each category card on the home page (used instead of the default icon).</p>
      {categoryGroups.map(group => (
        <div key={group.kind} className="mb-8">
          <h3 className="text-xl font-['Fredoka'] font-bold mb-3">{group.label}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {group.items.map(name => {
              const key = `${group.kind}:${name}`;
              const img = images[key];
              return (
                <div key={key} className="c4t-card !p-3" data-testid={`cat-img-card-${key.replace(/[^a-z0-9]+/gi,'-')}`}>
                  <div className="aspect-square overflow-hidden rounded-2xl bg-white border-2 border-black/30 flex items-center justify-center">
                    {img ? <img src={img} alt={name} className="w-full h-full object-cover" /> : <span className="c4t-desc text-xs text-center px-2">No image</span>}
                  </div>
                  <p className="text-sm font-bold mt-2 text-center">{name}</p>
                  <div className="flex gap-1 mt-2">
                    <label className="c4t-pill !py-1 !px-2 !text-xs flex-1 cursor-pointer justify-center">
                      <ImageIcon className="w-3 h-3" /> Upload
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(key, e.target.files[0])} data-testid={`upload-${key.replace(/[^a-z0-9]+/gi,'-')}`} />
                    </label>
                    {img && <button onClick={() => clear(key)} className="c4t-pill !py-1 !px-2 !text-xs !bg-[#FF1F87] !text-white"><Trash2 className="w-3 h-3" /></button>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

const ContentTab = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/settings").then(r => setS(r.data)); }, []);
  if (!s) return (
    <div data-testid="admin-content-tab" className="animate-pulse space-y-3">
      <div className="h-7 bg-gray-100 rounded w-40" />
      <div className="h-4 bg-gray-100 rounded w-72" />
      {[...Array(5)].map((_, i) => (
        <div key={i} className="c4t-card space-y-3">
          <div className="h-5 bg-gray-100 rounded w-1/3" />
          <div className="h-10 bg-gray-100 rounded" />
          <div className="h-10 bg-gray-100 rounded" />
        </div>
      ))}
    </div>
  );
  const set = (k, v) => setS({ ...s, content: { ...s.content, [k]: v } });
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", { content: s.content });
      toast.success("Content saved");
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };
  const groups = [
    { title: "Hero", keys: ["hero.eyebrow", "hero.title", "hero.subtitle", "hero.cta_primary", "hero.cta_secondary", "hero.feature_1", "hero.feature_2", "hero.feature_3", "hero.badge_left", "hero.badge_right"] },
    { title: "Section: Charts by Subject", keys: ["section.charts.title", "section.charts.subtitle"] },
    { title: "Section: Shop by Grade", keys: ["section.grade.title", "section.grade.subtitle"] },
    { title: "Section: Class Decor", keys: ["section.decor.title", "section.decor.subtitle"] },
    { title: "Section: Teacher Essentials", keys: ["section.essentials.title", "section.essentials.subtitle"] },
    { title: "Section: Graduation", keys: ["section.graduation.title", "section.graduation.subtitle", "section.graduation.empty_title", "section.graduation.empty_subtitle"] },
    { title: "Section: Featured", keys: ["section.featured.title", "section.featured.subtitle", "section.featured.see_all"] },
    { title: "Banner", keys: ["banner.title", "banner.subtitle", "banner.cta"] },
    { title: "About Page", keys: ["about.eyebrow", "about.title", "about.p1", "about.p2", "about.p3", "about.value1.title", "about.value1.desc", "about.value2.title", "about.value2.desc", "about.value3.title", "about.value3.desc", "about.cta_title", "about.cta_button"] },
    { title: "Contact Page", keys: ["contact.title", "contact.subtitle", "contact.follow_label"] },
    { title: "Shipping Page", keys: ["shipping.title", "shipping.subtitle", "shipping.note_title"] },
    { title: "Cart", keys: ["cart.title", "cart.usd_note", "cart.empty_title", "cart.empty_subtitle", "cart.empty_cta"] },
    { title: "Checkout", keys: ["checkout.title", "checkout.signed_in_note"] },
    { title: "Order Success", keys: ["success.title", "success.subtitle", "success.cta"] },
    { title: "Login", keys: ["login.title", "login.subtitle", "login.cta"] },
    { title: "Footer", keys: ["footer.tagline", "footer.heart_text"] },
  ];
  return (
    <div data-testid="admin-content-tab">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-['Fredoka'] font-bold">Content Editor</h2>
        <button type="button" onClick={save} disabled={busy} className="c4t-btn c4t-btn-pink" data-testid="save-content-btn">{busy ? "Saving..." : "Save All"}</button>
      </div>
      <p className="c4t-desc mb-6">Edit any text on the website. Changes apply immediately after saving.</p>
      {groups.map(g => (
        <details key={g.title} className="c4t-card mb-3" open>
          <summary className="cursor-pointer font-['Fredoka'] font-bold text-lg">{g.title}</summary>
          <div className="mt-3 space-y-3">
            {g.keys.map(k => (
              <div key={k}>
                <label className="text-xs c4t-desc font-bold">{k}</label>
                {(s.content[k]?.length || 0) > 80 ? (
                  <textarea className="c4t-input mt-1" rows={2} value={s.content[k] || ""} onChange={e => set(k, e.target.value)} data-testid={`content-${k}`} />
                ) : (
                  <input className="c4t-input mt-1" value={s.content[k] || ""} onChange={e => set(k, e.target.value)} data-testid={`content-${k}`} />
                )}
              </div>
            ))}
          </div>
        </details>
      ))}
    </div>
  );
};

const SettingsTab = () => {
  const [s, setS] = useState(null);
  const [expandedOpt, setExpandedOpt] = useState(null);
  const { refresh } = useSettings();
  useEffect(() => { api.get("/settings").then(r => setS(r.data)); }, []);
  if (!s) return (
    <div className="animate-pulse space-y-4 max-w-2xl">
      <div className="h-7 bg-gray-100 rounded w-32" />
      {[...Array(4)].map((_, i) => (
        <div key={i}>
          <div className="h-4 bg-gray-100 rounded w-40 mb-2" />
          <div className="h-10 bg-gray-100 rounded" />
        </div>
      ))}
      <div className="h-10 bg-gray-100 rounded w-32" />
    </div>
  );
  const save = async (e) => {
    e.preventDefault();
    try {
      const { category_images, ...payload } = s;
      await api.put("/admin/settings", payload);
      toast.success("Settings saved");
      refresh();
    } catch { toast.error("Save failed"); }
  };
  const setOpt = (idx, key, val) => {
    const opts = [...(s.shipping_options || [])];
    opts[idx] = { ...opts[idx], [key]: key === "price" ? parseFloat(val) || 0 : val };
    setS({ ...s, shipping_options: opts });
  };
  const addOpt = () => {
    const opts = [...(s.shipping_options || []), { name: "New option", price: 0, sub_options: [], fields: [] }];
    setS({ ...s, shipping_options: opts });
    setExpandedOpt(opts.length - 1);
  };
  const delOpt = (idx) => {
    setS({ ...s, shipping_options: s.shipping_options.filter((_, i) => i !== idx) });
    if (expandedOpt === idx) setExpandedOpt(null);
  };
  const addSubOpt = (idx) => {
    const opts = [...(s.shipping_options || [])];
    opts[idx] = { ...opts[idx], sub_options: [...(opts[idx].sub_options || []), { location: "", price: 0 }] };
    setS({ ...s, shipping_options: opts });
  };
  const setSubOpt = (optIdx, subIdx, key, val) => {
    const opts = [...(s.shipping_options || [])];
    const subs = [...(opts[optIdx].sub_options || [])];
    subs[subIdx] = { ...subs[subIdx], [key]: key === "price" ? parseFloat(val) || 0 : val };
    opts[optIdx] = { ...opts[optIdx], sub_options: subs };
    setS({ ...s, shipping_options: opts });
  };
  const delSubOpt = (optIdx, subIdx) => {
    const opts = [...(s.shipping_options || [])];
    opts[optIdx] = { ...opts[optIdx], sub_options: opts[optIdx].sub_options.filter((_, i) => i !== subIdx) };
    setS({ ...s, shipping_options: opts });
  };
  const addField = (idx) => {
    const opts = [...(s.shipping_options || [])];
    opts[idx] = { ...opts[idx], fields: [...(opts[idx].fields || []), { label: "", placeholder: "", required: true }] };
    setS({ ...s, shipping_options: opts });
  };
  const setField = (optIdx, fIdx, key, val) => {
    const opts = [...(s.shipping_options || [])];
    const fields = [...(opts[optIdx].fields || [])];
    fields[fIdx] = { ...fields[fIdx], [key]: val };
    opts[optIdx] = { ...opts[optIdx], fields };
    setS({ ...s, shipping_options: opts });
  };
  const delField = (optIdx, fIdx) => {
    const opts = [...(s.shipping_options || [])];
    opts[optIdx] = { ...opts[optIdx], fields: opts[optIdx].fields.filter((_, i) => i !== fIdx) };
    setS({ ...s, shipping_options: opts });
  };
  return (
    <form onSubmit={save} className="space-y-4 max-w-2xl" data-testid="admin-settings-tab">
      <h2 className="text-2xl font-['Fredoka'] font-bold">Settings</h2>

      <div>
        <label className="text-sm font-bold">Admin Name</label>
        <p className="text-xs c4t-desc mb-1">Your name — shown in the welcome banner on the admin dashboard.</p>
        <input className="c4t-input mt-1" placeholder="e.g. Mario-Lyn" value={s.admin_name || ""} onChange={e => setS({ ...s, admin_name: e.target.value })} data-testid="settings-admin-name" />
      </div>

      <div>
        <label className="text-sm font-bold">Exchange Rate (JMD per 1 USD)</label>
        <input type="number" step="0.01" className="c4t-input mt-1" value={s.exchange_rate ?? 160} onChange={e => setS({ ...s, exchange_rate: parseFloat(e.target.value) || 0 })} data-testid="settings-exchange-rate" />
      </div>

      <div>
        <label className="text-sm font-bold">Announcement Bar</label>
        <p className="text-xs c4t-desc mb-1">Displays a scrolling banner at the very top of every page. Leave blank to hide.</p>
        <textarea rows={2} className="c4t-input mt-1" placeholder="e.g. Orders placed before 5:30PM are processed same day&#10;Bank transfer & PayPal accepted" value={s.announcement_bar || ""} onChange={e => setS({ ...s, announcement_bar: e.target.value })} data-testid="settings-announcement-bar" />
      </div>

      <div>
        <div className="flex items-center gap-3 mb-1 flex-wrap">
          <label className="text-sm font-bold">Shipping Options</label>
          <span className="text-xs font-semibold bg-[#FF1F87] text-white rounded-full px-2 py-0.5">J$</span>
        </div>
        <p className="text-xs c4t-desc mb-2">Add sub-options for location-specific pricing, or required fields customers must fill at checkout.</p>
        <div className="space-y-3 mt-2" data-testid="settings-shipping-options">
          {(s.shipping_options || []).map((opt, i) => (
            <div key={i} className="border-2 border-black rounded-2xl overflow-hidden" data-testid={`shipping-option-${i}`}>
              <div className="flex gap-2 items-center p-3 bg-white">
                <input className="c4t-input flex-1 !mb-0" value={opt.name} onChange={e => setOpt(i, "name", e.target.value)} placeholder="Option name" />
                <span className="font-bold text-sm">$</span>
                <input type="number" step="0.01" className="c4t-input !w-24 !mb-0 min-w-0" value={opt.price || ""} onChange={e => setOpt(i, "price", parseFloat(e.target.value) || 0)} placeholder="0 JMD"  />
                <button type="button" onClick={() => setExpandedOpt(expandedOpt === i ? null : i)} className="c4t-pill !py-1 !px-2">
                  {expandedOpt === i ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                <button type="button" onClick={() => delOpt(i)} className="c4t-pill !py-1 !px-2 !bg-[#FF1F87] !text-white"><Trash2 className="w-3 h-3" /></button>
              </div>
              {expandedOpt === i && (
                <div className="bg-[#FFF9F2] border-t-2 border-black p-3 space-y-4">
                  {/* Sub-options */}
                  <div>
                    <p className="text-xs font-bold uppercase text-[#455A64] mb-2">Location Sub-Options <span className="font-normal normal-case">(overrides base price)</span></p>
                    <div className="space-y-2">
                      {(opt.sub_options || []).map((sub, si) => (
                        <div key={si} className="flex gap-2 items-center">
                          <input className="c4t-input flex-1 !mb-0 !text-sm" value={sub.location} onChange={e => setSubOpt(i, si, "location", e.target.value)} placeholder="e.g. Kingston, Portmore" />
                          <span className="font-bold text-sm">$</span>
                          <input type="number" step="0.01" className="c4t-input !w-24 !mb-0 min-w-0 !text-sm" value={sub.price || ""} onChange={e => setSubOpt(i, si, "price", parseFloat(e.target.value) || 0)} placeholder="0 JMD"  />
                          <button type="button" onClick={() => delSubOpt(i, si)} className="c4t-pill !py-1 !px-2 !bg-[#FF1F87] !text-white"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      ))}
                    </div>
                    <button type="button" onClick={() => addSubOpt(i)} className="c4t-pill !text-xs mt-2"><Plus className="w-3 h-3" /> Add location</button>
                  </div>
                  {/* Required fields */}
                  <div>
                    <p className="text-xs font-bold uppercase text-[#455A64] mb-2">Required Fields <span className="font-normal normal-case">(customer fills these at checkout)</span></p>
                    <div className="space-y-2">
                      {(opt.fields || []).map((f, fi) => (
                        <div key={fi} className="flex gap-2 items-center flex-wrap">
                          <input className="c4t-input flex-1 !mb-0 !text-sm" value={f.label} onChange={e => setField(i, fi, "label", e.target.value)} placeholder="Field label (e.g. Parish)" />
                          <input className="c4t-input flex-1 !mb-0 !text-sm" value={f.placeholder} onChange={e => setField(i, fi, "placeholder", e.target.value)} placeholder="Placeholder (e.g. Kingston)" />
                          <label className="flex items-center gap-1 text-xs font-semibold cursor-pointer">
                            <input type="checkbox" checked={f.required} onChange={e => setField(i, fi, "required", e.target.checked)} /> Required
                          </label>
                          <button type="button" onClick={() => delField(i, fi)} className="c4t-pill !py-1 !px-2 !bg-[#FF1F87] !text-white"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      ))}
                    </div>
                    <button type="button" onClick={() => addField(i)} className="c4t-pill !text-xs mt-2"><Plus className="w-3 h-3" /> Add field</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        <button type="button" onClick={addOpt} className="c4t-pill mt-3" data-testid="settings-add-shipping"><Plus className="w-3 h-3" /> Add shipping option</button>
      </div>

      <div>
        <label className="text-sm font-bold">Bank Transfer Instructions</label>
        <textarea rows={6} className="c4t-input mt-1" value={s.bank_instructions} onChange={e => setS({ ...s, bank_instructions: e.target.value })} data-testid="settings-bank" />
      </div>
      <div>
        <label className="text-sm font-bold">Shipping Note</label>
        <textarea rows={3} className="c4t-input mt-1" value={s.shipping_note} onChange={e => setS({ ...s, shipping_note: e.target.value })} data-testid="settings-shipping" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <input className="c4t-input" placeholder="Email" value={s.contact_email} onChange={e => setS({ ...s, contact_email: e.target.value })} />
        <input className="c4t-input" placeholder="WhatsApp" value={s.whatsapp} onChange={e => setS({ ...s, whatsapp: e.target.value })} />
        <input className="c4t-input" placeholder="Instagram URL" value={s.instagram} onChange={e => setS({ ...s, instagram: e.target.value })} />
        <input className="c4t-input" placeholder="Facebook URL" value={s.facebook} onChange={e => setS({ ...s, facebook: e.target.value })} />
        <input className="c4t-input" placeholder="TikTok URL" value={s.tiktok} onChange={e => setS({ ...s, tiktok: e.target.value })} />
      </div>
      <div className="c4t-card !p-4 border-2 border-[#FF1F87]/30">
        <h3 className="font-['Fredoka'] font-bold text-lg mb-1">Bulk Discount</h3>
        <p className="c4t-desc text-sm mb-3">Automatically give a percentage off when a customer has enough items in their cart. Does not apply if a promo code is used.</p>
        <label className="flex items-center gap-3 mb-3 cursor-pointer">
          <input type="checkbox" className="w-4 h-4" checked={!!s.bulk_discount_enabled} onChange={e => setS({ ...s, bulk_discount_enabled: e.target.checked })} data-testid="settings-bulk-enabled" />
          <span className="text-sm font-medium">Enable bulk discount</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="c4t-label">Minimum items in cart</label>
            <input type="number" min="1" step="1" className="c4t-input" value={s.bulk_discount_min_items ?? 3} onChange={e => setS({ ...s, bulk_discount_min_items: parseInt(e.target.value) || 0 })} data-testid="settings-bulk-min" placeholder="3" />
          </div>
          <div>
            <label className="c4t-label">Discount (%)</label>
            <input type="number" min="0" max="100" step="0.5" className="c4t-input" value={s.bulk_discount_percent ?? 0} onChange={e => setS({ ...s, bulk_discount_percent: parseFloat(e.target.value) || 0 })} data-testid="settings-bulk-percent" placeholder="10" />
          </div>
        </div>
        <p className="c4t-desc text-xs mt-2">Example: minimum 3 items, 10% off — a customer with 3 or more items automatically gets 10% off their subtotal at checkout.</p>
      </div>
      <button type="submit" className="c4t-btn c4t-btn-pink" data-testid="settings-save-btn">Save Settings</button>
    </form>
  );
};

const BundlesTab = () => {
  const [bundles, setBundles] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", price: "", image: "", product_ids: [], active: true });
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/bundles").then(r => setBundles(r.data || [])).catch(() => {});
  const loadProducts = () => api.get("/admin/products/light").then(r => { const d = r.data; setProducts(Array.isArray(d) ? d : (d.items || [])); }).catch(() => {});
  useEffect(() => { load(); loadProducts(); }, []);

  const toggleProduct = (id) => setForm(f => ({ ...f, product_ids: f.product_ids.includes(id) ? f.product_ids.filter(x => x !== id) : [...f.product_ids, id] }));

  const onImage = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const r = new FileReader();
    r.onload = () => setForm(f => ({ ...f, image: r.result }));
    r.readAsDataURL(file);
  };

  const reset = () => { setForm({ name: "", description: "", price: "", image: "", product_ids: [], active: true }); setEditingId(null); };

  const normalTotal = form.product_ids.reduce((sum, id) => { const p = products.find(x => x.id === id); return sum + (p ? (Number(p.price) || 0) : 0); }, 0);
  const bundlePrice = Number(form.price) || 0;
  const saving = Math.max(0, normalTotal - bundlePrice);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Bundle name is required"); return; }
    if (!form.product_ids.length) { toast.error("Pick at least one product"); return; }
    setBusy(true);
    const payload = { name: form.name.trim(), description: form.description, image: form.image, price: bundlePrice, product_ids: form.product_ids, active: form.active };
    try {
      if (editingId) { await api.put("/admin/bundles/" + editingId, payload); toast.success("Bundle updated"); }
      else { await api.post("/admin/bundles", payload); toast.success("Bundle created"); }
      reset(); load();
    } catch (err) { toast.error((err && err.response && err.response.data && err.response.data.detail) || "Could not save bundle"); }
    setBusy(false);
  };

  const edit = (b) => { setEditingId(b.id); setForm({ name: b.name || "", description: b.description || "", price: String(b.price != null ? b.price : ""), image: b.image || "", product_ids: b.product_ids || [], active: b.active !== false }); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const remove = async (id) => { if (!window.confirm("Delete this bundle?")) return; try { await api.delete("/admin/bundles/" + id); toast.success("Bundle deleted"); load(); } catch (e) { toast.error("Could not delete"); } };

  const jmd = (n) => "J$" + (Number(n) || 0).toLocaleString();

  return (
    <div>
      <h2 className="text-2xl font-['Fredoka'] font-bold mb-4 flex items-center gap-2"><Layers className="w-6 h-6 text-[#FFC500]" /> Bundles</h2>

      <form onSubmit={submit} className="c4t-card mb-6">
        <div className="font-['Fredoka'] font-bold text-lg mb-3">{editingId ? "Edit Bundle" : "New Bundle"}</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-bold">Bundle Name *</label>
            <input className="c4t-input mt-1" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className="text-sm font-bold">Bundle Price (J$) *</label>
            <input type="number" min="0" className="c4t-input mt-1" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} />
          </div>
        </div>
        <div className="mt-3">
          <label className="text-sm font-bold">Description</label>
          <textarea className="c4t-input mt-1" rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
        </div>
        <div className="mt-3">
          <label className="text-sm font-bold">Cover Image</label>
          <input type="file" accept="image/*" className="c4t-input mt-1" onChange={onImage} />
          {form.image && <img src={form.image} alt="" className="w-24 h-24 object-cover rounded-lg mt-2 border-2 border-black" />}
        </div>
        <div className="mt-4">
          <div className="text-sm font-bold mb-2">Included Products *</div>
          <div className="grid sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto border-2 border-black rounded-lg p-3">
            {products.map(p => (
              <label key={p.id} className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="checkbox" checked={form.product_ids.includes(p.id)} onChange={() => toggleProduct(p.id)} />
                <span className="flex-1">{p.name}</span>
                <span className="text-[#888]">{jmd(p.price)}</span>
              </label>
            ))}
            {!products.length && <div className="text-sm text-[#888]">No products yet.</div>}
          </div>
        </div>
        <div className="mt-4 c4t-card !bg-[#FFF1F7] grid sm:grid-cols-3 gap-3 text-center">
          <div><div className="text-xs text-[#888]">Normal total</div><div className="font-bold">{jmd(normalTotal)}</div></div>
          <div><div className="text-xs text-[#888]">Bundle price</div><div className="font-bold">{jmd(bundlePrice)}</div></div>
          <div><div className="text-xs text-[#888]">Customer saves</div><div className="font-bold text-[#06D6A0]">{jmd(saving)}</div></div>
        </div>
        <label className="flex items-center gap-2 mt-3 text-sm font-bold cursor-pointer">
          <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} /> Show in shop (active)
        </label>
        <div className="flex gap-2 mt-4">
          <button type="submit" disabled={busy} className="c4t-btn c4t-btn-pink">{editingId ? "Save Changes" : "Create Bundle"}</button>
          {editingId && <button type="button" onClick={reset} className="c4t-btn">Cancel</button>}
        </div>
      </form>

      <div className="c4t-card">
        <div className="font-['Fredoka'] font-bold text-lg mb-3">Existing Bundles</div>
        {!bundles.length && <div className="text-sm text-[#888]">No bundles yet.</div>}
        <div className="space-y-3">
          {bundles.map(b => (
            <div key={b.id} className="border-2 border-black rounded-lg p-3 flex items-start gap-3">
              {b.image && <img src={b.image} alt="" className="w-16 h-16 object-cover rounded-lg border-2 border-black" />}
              <div className="flex-1">
                <div className="font-bold flex items-center gap-2">{b.name} {!b.active && <span className="text-xs bg-[#eee] px-2 py-0.5 rounded-full">Hidden</span>}</div>
                <div className="text-sm">{jmd(b.price)} <span className="text-[#888] line-through ml-1">{jmd(b.compare_at_price)}</span> <span className="text-[#06D6A0] ml-1">save {jmd(b.savings)}</span></div>
                <div className="text-xs text-[#888] mt-1">{(b.items || []).map(i => i.name).join(", ")}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => edit(b)} className="c4t-pill cursor-pointer"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(b.id)} className="c4t-pill cursor-pointer text-[#e63946]"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const PromosTab = () => {
  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState({ code: "", percent_off: 10, active: true, expires_at: "" });
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/promo-codes").then(r => setCodes(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      code: form.code.trim().toUpperCase(),
      percent_off: Number(form.percent_off),
      active: form.active,
      expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
    };
    if (!payload.code) { toast.error("Code is required"); return; }
    if (payload.percent_off <= 0 || payload.percent_off > 100) { toast.error("Percent must be between 1 and 100"); return; }
    setBusy(true);
    try {
      if (editingId) {
        await api.put(`/admin/promo-codes/${editingId}`, payload);
        toast.success("Promo code updated");
      } else {
        await api.post("/admin/promo-codes", payload);
        toast.success(`Promo code "${payload.code}" created`);
      }
      setForm({ code: "", percent_off: 10, active: true, expires_at: "" });
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({
      code: c.code,
      percent_off: c.percent_off,
      active: c.active,
      expires_at: c.expires_at ? c.expires_at.slice(0, 10) : "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleActive = async (c) => {
    try {
      await api.put(`/admin/promo-codes/${c.id}`, { active: !c.active });
      toast.success(c.active ? `"${c.code}" disabled` : `"${c.code}" activated`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete promo code "${c.code}"?`)) return;
    try {
      await api.delete(`/admin/promo-codes/${c.id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Failed to delete"); }
  };

  const isExpired = (c) => {
    if (!c.expires_at) return false;
    try { return new Date(c.expires_at) < new Date(); } catch { return false; }
  };

  return (
    <div data-testid="admin-promos-tab">
      <h2 className="text-2xl font-['Fredoka'] font-bold mb-4 flex items-center gap-2">
        <Tag className="w-6 h-6 text-[#FFC500]" /> Promo Codes
      </h2>

      <form onSubmit={submit} className="c4t-card mb-6" data-testid="promo-form">
        <h3 className="font-['Fredoka'] font-bold text-lg mb-3">
          {editingId ? "Edit promo code" : "Create new promo code"}
        </h3>
        <div className="grid sm:grid-cols-4 gap-3">
          <div>
            <label className="text-sm font-bold">Code</label>
            <input className="c4t-input mt-1 uppercase" placeholder="BACK2SCHOOL10" required
              value={form.code} onChange={e => setForm({ ...form, code: e.target.value })}
              data-testid="promo-form-code" />
          </div>
          <div>
            <label className="text-sm font-bold">% Off</label>
            <input type="number" min="1" max="100" className="c4t-input mt-1" required
              value={form.percent_off} onChange={e => setForm({ ...form, percent_off: e.target.value })}
              data-testid="promo-form-percent" />
          </div>
          <div>
            <label className="text-sm font-bold">Expires (optional)</label>
            <input type="date" className="c4t-input mt-1"
              value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })}
              data-testid="promo-form-expires" />
          </div>
          <div className="flex items-end">
            <label className="c4t-pill !justify-start cursor-pointer w-full">
              <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })}
                data-testid="promo-form-active" />
              {form.active ? "Live (visible)" : "Offline"}
            </label>
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <button type="submit" disabled={busy} className="c4t-btn c4t-btn-pink" data-testid="promo-form-submit">
            <Plus className="w-4 h-4" /> {busy ? "Saving..." : editingId ? "Update Code" : "Add Code"}
          </button>
          {editingId && (
            <button type="button" onClick={() => { setEditingId(null); setForm({ code: "", percent_off: 10, active: true, expires_at: "" }); }}
              className="c4t-pill" data-testid="promo-form-cancel">Cancel</button>
          )}
        </div>
      </form>

      <div className="grid gap-3">
        {codes.length === 0 ? (
          <p className="c4t-desc text-center py-8" data-testid="promos-empty">No promo codes yet. Create your first one above!</p>
        ) : codes.map(c => {
          const expired = isExpired(c);
          return (
            <div key={c.id} className="c4t-card flex flex-wrap items-center gap-4" data-testid={`promo-row-${c.code}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-['Fredoka'] font-bold text-xl">{c.code}</span>
                  <span className="c4t-pill !text-xs !py-0.5 !px-2 !bg-[#FFC500]">{c.percent_off}% OFF</span>
                  {c.active && !expired && <span className="c4t-pill !text-xs !py-0.5 !px-2 !bg-[#00E0AA]">LIVE</span>}
                  {!c.active && <span className="c4t-pill !text-xs !py-0.5 !px-2 !bg-[#455A64] !text-white">OFFLINE</span>}
                  {expired && <span className="c4t-pill !text-xs !py-0.5 !px-2 !bg-[#FF1F87] !text-white">EXPIRED</span>}
                </div>
                {c.expires_at && (
                  <p className="text-xs c4t-desc mt-1">
                    Expires: {new Date(c.expires_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                  </p>
                )}
              </div>
              <button onClick={() => toggleActive(c)} className="c4t-pill !py-1 !px-3" data-testid={`promo-toggle-${c.code}`}>
                <Power className="w-3 h-3" /> {c.active ? "Disable" : "Enable"}
              </button>
              <button onClick={() => startEdit(c)} className="c4t-pill !py-1 !px-3" data-testid={`promo-edit-${c.code}`}>
                <Pencil className="w-3 h-3" /> Edit
              </button>
              <button onClick={() => del(c)} className="c4t-pill !py-1 !px-3 !bg-[#FF1F87] !text-white" data-testid={`promo-delete-${c.code}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Vouchers ──────────────────────────────────────────────────────────────────
const VouchersTab = () => {
  const { exchangeRate } = useSettings();
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ code: "", value_usd: "", note: "", expires_at: "" });
  const [voucherCur, setVoucherCur] = useState("JMD");
  const [busy, setBusy] = useState(false);
  const load = () => { setLoading(true); api.get("/admin/vouchers").then(r => setVouchers(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.value_usd) { toast.error("Code and value are required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/vouchers", {
        code: form.code.trim().toUpperCase(),
        value: parseFloat(form.value_usd),
        currency: voucherCur,
        note: form.note,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      });
      toast.success("Voucher created");
      setForm({ code: "", value_usd: "", note: "", expires_at: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const del = async (v) => {
    const symb = v.currency === "JMD" ? "J$" : "$";
    if (!window.confirm(`Delete voucher "${v.code}"? Remaining balance: ${symb}${(v.current_balance || 0).toFixed(2)} ${v.currency || "USD"}`)) return;
    await api.delete(`/admin/vouchers/${v.id}`);
    toast.success("Deleted"); load();
  };

  const pct = (v) => (v.initial_value || 0) > 0 ? Math.round(((v.current_balance || 0) / v.initial_value) * 100) : 0;

  return (
    <div data-testid="admin-vouchers-tab">
      <h2 className="text-2xl font-['Fredoka'] font-bold mb-4 flex items-center gap-2">
        <Gift className="w-6 h-6 text-[#00E0AA]" /> Vouchers / Gift Cards
      </h2>
      <p className="c4t-desc mb-4 text-sm">Vouchers have a fixed monetary value in Jamaican dollars (J$) that decreases with each use. Customers enter the code at checkout.</p>

      <form onSubmit={submit} className="c4t-card mb-6">
        <h3 className="font-['Fredoka'] font-bold text-lg mb-3">Create new voucher</h3>
        <div className="grid sm:grid-cols-4 gap-3">
          <div>
            <label className="text-sm font-bold">Code</label>
            <input className="c4t-input mt-1 uppercase" placeholder="GIFT2025" required value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} />
          </div>
          <div>
            <label className="text-sm font-bold mb-1 block">Value (J$)</label>
            <input type="number" step="0.01" min="0.01" className="c4t-input" placeholder="20000.00" required value={form.value_usd} onChange={e => setForm({ ...form, value_usd: e.target.value })} />

          </div>
          <div>
            <label className="text-sm font-bold">Expires (optional)</label>
            <input type="date" className="c4t-input mt-1" value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })} />
          </div>
          <div>
            <label className="text-sm font-bold">Note (optional)</label>
            <input className="c4t-input mt-1" placeholder="Competition prize" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
          </div>
        </div>
        <button type="submit" disabled={busy} className="c4t-btn c4t-btn-green mt-4"><Plus className="w-4 h-4" /> {busy ? "Creating..." : "Create Voucher"}</button>
      </form>

      <div className="grid gap-3">
        {loading ? [...Array(3)].map((_, i) => (
          <div key={i} className="c4t-card animate-pulse h-16" />
        )) : vouchers.length === 0 ? (
          <p className="c4t-desc text-center py-8">No vouchers yet. Create your first one above!</p>
        ) : vouchers.map(v => {
          const symb = v.currency === "JMD" ? "J$" : "$";
          const spent = (v.initial_value || 0) - (v.current_balance || 0);
          const pctLeft = pct(v);
          const expired = v.expires_at && new Date(v.expires_at) < new Date();
          return (
            <div key={v.id} className="c4t-card flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-['Fredoka'] font-bold text-xl">{v.code}</span>
                  <span className="c4t-pill !py-0.5 !px-2 !text-xs">{v.currency || "USD"}</span>
                  {expired && <span className="c4t-pill !text-xs !py-0.5 !px-2 !bg-[#FF1F87] !text-white">EXPIRED</span>}
                  {(v.current_balance || 0) <= 0 && <span className="c4t-pill !text-xs !py-0.5 !px-2 !bg-gray-400 !text-white">DEPLETED</span>}
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-[#FF1F87] font-bold">{symb}{(v.current_balance || 0).toFixed(2)} remaining</span>
                  <span className="c4t-desc">{symb}{(v.initial_value || 0).toFixed(2)} original · {symb}{spent.toFixed(2)} used</span>
                </div>
                <div className="mt-1.5 h-2 bg-gray-100 rounded-full overflow-hidden w-48">
                  <div className="h-full bg-[#00E0AA] rounded-full transition-all" style={{ width: `${pctLeft}%` }} />
                </div>
                {v.note && <p className="text-xs c4t-desc mt-1">{v.note}</p>}
                {v.expires_at && <p className="text-xs c4t-desc">Expires: {new Date(v.expires_at).toLocaleDateString()}</p>}
              </div>
              <button onClick={() => del(v)} className="c4t-pill !py-1 !px-3 !bg-[#FF1F87] !text-white"><Trash2 className="w-3 h-3" /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Notifications ─────────────────────────────────────────────────────────────
const NotificationsTab = () => {
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const load = () => { setLoading(true); api.get("/admin/notifications").then(r => setNotifs(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) { toast.error("Subject and message are required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/notifications", form);
      toast.success("Notification pushed to all users!");
      setForm({ subject: "", message: "" });
      load();
    } catch { toast.error("Failed to push notification"); }
    finally { setBusy(false); }
  };

  const del = async (n) => {
    if (!window.confirm(`Delete notification "${n.subject}"?`)) return;
    await api.delete(`/admin/notifications/${n.id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-notifications-tab">
      <h2 className="text-2xl font-['Fredoka'] font-bold mb-4 flex items-center gap-2">
        <Megaphone className="w-6 h-6 text-[#FF1F87]" /> Push Notifications
      </h2>
      <p className="c4t-desc mb-4 text-sm">Push an announcement to all users. They'll see it in the notification bell on the site.</p>

      <form onSubmit={submit} className="c4t-card mb-6">
        <h3 className="font-['Fredoka'] font-bold text-lg mb-3">New notification</h3>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-bold">Subject</label>
            <input className="c4t-input mt-1" placeholder="New products just added!" required value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} />
          </div>
          <div>
            <label className="text-sm font-bold">Message</label>
            <textarea rows={3} className="c4t-input mt-1" placeholder="We just uploaded 12 new Math charts for Grades 1–3..." required value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} />
          </div>
        </div>
        <button type="submit" disabled={busy} className="c4t-btn c4t-btn-pink mt-4"><Megaphone className="w-4 h-4" /> {busy ? "Pushing..." : "Push to All Users"}</button>
      </form>

      <div className="space-y-3">
        {loading ? [...Array(2)].map((_, i) => <div key={i} className="c4t-card animate-pulse h-20" />) :
        notifs.length === 0 ? <p className="c4t-desc text-center py-8">No notifications sent yet.</p> :
        notifs.map(n => (
          <div key={n.id} className="c4t-card flex gap-4 items-start">
            <div className="flex-1">
              <p className="font-bold">{n.subject}</p>
              <p className="c4t-desc text-sm mt-1">{n.message}</p>
              <p className="text-xs c4t-desc mt-2">{new Date(n.created_at).toLocaleString()}</p>
            </div>
            <button onClick={() => del(n)} className="c4t-pill !py-1 !px-2 !bg-[#FF1F87] !text-white shrink-0"><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

function StatsCards({ stats }) {
  const { exchangeRate } = useSettings();
  const [revCur, setRevCur] = React.useState("JMD");
  const revenueUsd = stats ? Number(stats.total_revenue_usd) : null;
  const revenueDisplay = revenueUsd === null ? "—"
    : revCur === "JMD"
      ? `J$${Math.round(revenueUsd * (exchangeRate || 160)).toLocaleString("en-US")} JMD`
      : `$${revenueUsd.toLocaleString("en-US", {minimumFractionDigits:2,maximumFractionDigits:2})} USD`;
  const cards = [
    { label: "Total Orders", value: stats?.total_orders ?? "—", icon: <ShoppingBag className="w-5 h-5" />, color: "#FF1F87" },
    { label: "Pending Orders", value: stats?.pending_orders ?? "—", icon: <Package className="w-5 h-5" />, color: "#f59e0b" },

    { label: "Products", value: stats?.total_products ?? "—", icon: <BarChart2 className="w-5 h-5" />, color: "#6366f1" },
    { label: "Customers", value: stats?.total_customers ?? "—", icon: <Users className="w-5 h-5" />, color: "#0ea5e9" },
    { label: "Unread Messages", value: stats?.unread_messages ?? "—", icon: <MessageSquare className="w-5 h-5" />, color: stats?.unread_messages > 0 ? "#FF1F87" : "#6b7280" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
      {cards.map(card => (
        <div key={card.label} className="c4t-card !p-4 flex flex-col gap-1">
          <div className="flex items-center gap-2" style={{ color: card.color }}>
            {card.icon}
            <span className="text-xs font-semibold uppercase tracking-wide opacity-80">{card.label}</span>
          </div>
          <p className="text-2xl font-['Fredoka'] font-bold mt-1" style={{ color: card.color }}>{card.value}</p>
          {card.extra && card.extra}
        </div>
      ))}
    </div>
  );
}


// ── Reports Tab ──────────────────────────────────────────────────────────────
const ReportsTab = () => {
  const { exchangeRate } = useSettings();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [revCur, setRevCur] = React.useState("JMD");

  React.useEffect(() => {
    api.get("/admin/reports")
      .then(r => setData(r.data))
      .catch(() => toast.error("Could not load reports"))
      .finally(() => setLoading(false));
  }, []);

  // Build last-12-months label array
  const last12Months = React.useMemo(() => {
    const months = [];
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(d.toISOString().slice(0, 7)); // "YYYY-MM"
    }
    return months;
  }, []);

  // Merge physical + digital monthly data into unified array
  const chartData = React.useMemo(() => {
    if (!data) return [];
    const physMap = Object.fromEntries(data.monthly.physical.map(r => [r.month, r]));
    const digMap  = Object.fromEntries(data.monthly.digital.map(r => [r.month, r]));
    return last12Months.map(m => {
      const label = new Date(m + "-01").toLocaleDateString("en-US", { month: "short", year: "2-digit" });
      const physRev = physMap[m]?.revenue ?? 0;
      const digRev  = digMap[m]?.revenue ?? 0;
      const totalRev = revCur === "JMD"
        ? (physRev + digRev) * (exchangeRate || 160)
        : (physRev + digRev);
      const physRevDisplay = revCur === "JMD" ? physRev * (exchangeRate || 160) : physRev;
      const digRevDisplay  = revCur === "JMD" ? digRev  * (exchangeRate || 160) : digRev;
      return {
        month: label,
        "Physical": Math.round(physRevDisplay * 100) / 100,
        "Digital":  Math.round(digRevDisplay  * 100) / 100,
        "Total": Math.round(totalRev * 100) / 100,
        physOrders: physMap[m]?.orders ?? 0,
        digOrders:  digMap[m]?.orders  ?? 0,
      };
    });
  }, [data, last12Months, revCur, exchangeRate]);

  const fmtRev = (v) => revCur === "JMD"
    ? `J$${Math.round(v).toLocaleString()}`
    : `$${Number(v).toFixed(2)}`;

  const fmtUSD = (v) => `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const COLORS = ["#FF1F87", "#6366f1", "#10b981", "#f59e0b", "#0ea5e9", "#8b5cf6"];
  const STATUS_COLORS = { Shipped: "#10b981", Completed: "#0ea5e9", Pending: "#f59e0b", Processing: "#6366f1", Cancelled: "#ef4444" };

  if (loading) return (
    <div className="animate-pulse space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[1,2,3,4].map(i => <div key={i} className="h-24 rounded-2xl bg-gray-100" />)}
      </div>
      <div className="h-64 rounded-2xl bg-gray-100" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="h-48 rounded-2xl bg-gray-100" />
        <div className="h-48 rounded-2xl bg-gray-100" />
      </div>
    </div>
  );

  if (!data) return <p className="text-center py-12" style={{ color: "var(--text-secondary)" }}>No report data available.</p>;

  const { totals, top_products, payment_methods, currency_split, order_status, discounts } = data;

  // KPI cards
  const physRevDisplay = revCur === "JMD"
    ? `J$${Math.round(totals.physical_revenue_usd * (exchangeRate || 160)).toLocaleString()} JMD`
    : fmtUSD(totals.physical_revenue_usd) + " USD";
  const digRevDisplay = revCur === "JMD"
    ? `J$${Math.round(totals.digital_revenue_usd * (exchangeRate || 160)).toLocaleString()} JMD`
    : fmtUSD(totals.digital_revenue_usd) + " USD";
  const combinedDisplay = revCur === "JMD"
    ? `J$${Math.round(totals.combined_revenue_usd * (exchangeRate || 160)).toLocaleString()} JMD`
    : fmtUSD(totals.combined_revenue_usd) + " USD";

  const avgOrderUsd = totals.total_physical_orders > 0 ? totals.physical_revenue_usd / totals.total_physical_orders : 0;
  const avgDisplay = revCur === "JMD"
    ? `J$${Math.round(avgOrderUsd * (exchangeRate || 160)).toLocaleString()}`
    : fmtUSD(avgOrderUsd);

  return (
    <div data-testid="admin-reports-tab" className="space-y-5">
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-['Fredoka'] font-bold" style={{ color: "var(--text)" }}>Reports & Analytics</h2>
          <p className="text-sm mt-0.5" style={{ color: "var(--text-secondary)" }}>Revenue trends, top products, and order insights</p>
        </div>
        <div className="flex gap-1 p-1 rounded-xl" style={{ background: "#f3f4f6" }}>
          {["USD", "JMD"].map(c => (
            <button key={c} onClick={() => setRevCur(c)}
              className="text-xs px-3 py-1.5 rounded-lg font-semibold transition-all"
              style={revCur === c ? { background: "#FF1F87", color: "#fff" } : { color: "var(--text-secondary)" }}>
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: "Combined Revenue", value: combinedDisplay, icon: <TrendingUp className="w-5 h-5" />, color: "#FF1F87", sub: "All time · physical + digital" },
          { label: "Physical Revenue", value: physRevDisplay, icon: <ShoppingBag className="w-5 h-5" />, color: "#6366f1", sub: `${totals.total_physical_orders} orders` },
          { label: "Digital Revenue", value: digRevDisplay, icon: <FileText className="w-5 h-5" />, color: "#10b981", sub: `${totals.total_digital_sales} sales` },
          { label: "Avg Order Value", value: avgDisplay, icon: <BarChart2 className="w-5 h-5" />, color: "#f59e0b", sub: "Physical orders only" },
        ].map(card => (
          <div key={card.label} className="c4t-card !p-4">
            <div className="flex items-center gap-2 mb-2" style={{ color: card.color }}>
              {card.icon}
              <span className="text-xs font-semibold uppercase tracking-wide">{card.label}</span>
            </div>
            <p className="text-xl font-['Fredoka'] font-bold leading-tight" style={{ color: card.color }}>{card.value}</p>
            <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>{card.sub}</p>
          </div>
        ))}
      </div>

      {/* Revenue trend chart */}
      <div className="c4t-card !p-5">
        <h3 className="text-base font-semibold mb-4" style={{ color: "var(--text)" }}>Revenue Trend — Last 12 Months</h3>
        <RechartsBarChart data={chartData} revCur={revCur} fmtRev={fmtRev} />
      </div>

      {/* Top products */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="c4t-card !p-5">
          <h3 className="text-base font-semibold mb-3" style={{ color: "var(--text)" }}>Top Physical Products</h3>
          {top_products.physical.length === 0
            ? <p className="text-sm" style={{ color: "var(--text-secondary)" }}>No completed orders yet.</p>
            : top_products.physical.map((p, i) => (
              <div key={p.name} className="flex items-center gap-3 py-2.5 border-b last:border-0" style={{ borderColor: "#f3f4f6" }}>
                <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                  style={{ background: COLORS[i % COLORS.length] }}>{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: "var(--text)" }}>{p.name}</p>
                  <p className="text-xs" style={{ color: "var(--text-secondary)" }}>{p.qty} units sold</p>
                </div>
                <span className="text-sm font-semibold shrink-0" style={{ color: "#10b981" }}>
                  {revCur === "JMD" ? `J$${Math.round(p.revenue_usd * (exchangeRate || 160)).toLocaleString()}` : fmtUSD(p.revenue_usd)}
                </span>
              </div>
            ))
          }
        </div>
        <div className="c4t-card !p-5">
          <h3 className="text-base font-semibold mb-3" style={{ color: "var(--text)" }}>Top Digital Products</h3>
          {top_products.digital.length === 0
            ? <p className="text-sm" style={{ color: "var(--text-secondary)" }}>No digital sales yet.</p>
            : top_products.digital.map((p, i) => (
              <div key={p.name} className="flex items-center gap-3 py-2.5 border-b last:border-0" style={{ borderColor: "#f3f4f6" }}>
                <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                  style={{ background: COLORS[i % COLORS.length] }}>{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: "var(--text)" }}>{p.name}</p>
                  <p className="text-xs" style={{ color: "var(--text-secondary)" }}>{p.purchases} purchase{p.purchases !== 1 ? "s" : ""}</p>
                </div>
                <span className="text-sm font-semibold shrink-0" style={{ color: "#10b981" }}>
                  {revCur === "JMD" ? `J$${Math.round(p.revenue_usd * (exchangeRate || 160)).toLocaleString()}` : fmtUSD(p.revenue_usd)}
                </span>
              </div>
            ))
          }
        </div>
      </div>

      {/* Bottom row: status, payments, discounts */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Order status */}
        <div className="c4t-card !p-5">
          <h3 className="text-base font-semibold mb-3" style={{ color: "var(--text)" }}>Orders by Status</h3>
          <div className="space-y-2">
            {order_status.map(s => {
              const total = order_status.reduce((a, b) => a + b.count, 0);
              const pct = total > 0 ? Math.round((s.count / total) * 100) : 0;
              const color = STATUS_COLORS[s.status] || "#9ca3af";
              return (
                <div key={s.status}>
                  <div className="flex justify-between text-sm mb-1">
                    <span style={{ color: "var(--text)" }}>{s.status}</span>
                    <span className="font-semibold" style={{ color }}>{s.count} <span className="font-normal text-xs" style={{ color: "var(--text-secondary)" }}>({pct}%)</span></span>
                  </div>
                  <div className="h-1.5 rounded-full" style={{ background: "#f3f4f6" }}>
                    <div className="h-1.5 rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Payment methods */}
        <div className="c4t-card !p-5">
          <h3 className="text-base font-semibold mb-3" style={{ color: "var(--text)" }}>Payment Methods</h3>
          <div className="space-y-2">
            {payment_methods.map((p, i) => {
              const total = payment_methods.reduce((a, b) => a + b.count, 0);
              const pct = total > 0 ? Math.round((p.count / total) * 100) : 0;
              const color = i === 0 ? "#0070ba" : i === 1 ? "#10b981" : COLORS[i];
              return (
                <div key={p.method}>
                  <div className="flex justify-between text-sm mb-1">
                    <span style={{ color: "var(--text)" }}>{p.method || "Unknown"}</span>
                    <span className="font-semibold" style={{ color }}>{p.count} <span className="font-normal text-xs" style={{ color: "var(--text-secondary)" }}>({pct}%)</span></span>
                  </div>
                  <div className="h-1.5 rounded-full" style={{ background: "#f3f4f6" }}>
                    <div className="h-1.5 rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
                  </div>
                </div>
              );
            })}
            {payment_methods.length === 0 && <p className="text-sm" style={{ color: "var(--text-secondary)" }}>No orders yet.</p>}
          </div>

          {/* Currency split */}
          <h3 className="text-base font-semibold mb-3 mt-5" style={{ color: "var(--text)" }}>Currency Split</h3>
          <div className="flex gap-2 flex-wrap">
            {currency_split.map(c => (
              <div key={c.currency} className="flex items-center gap-2 px-3 py-2 rounded-xl" style={{ background: c.currency === "JMD" ? "#fef3c7" : "#eff6ff" }}>
                <span className="text-sm font-bold" style={{ color: c.currency === "JMD" ? "#d97706" : "#3b82f6" }}>{c.currency}</span>
                <span className="text-sm" style={{ color: "var(--text)" }}>{c.count} order{c.count !== 1 ? "s" : ""}</span>
              </div>
            ))}
            {currency_split.length === 0 && <p className="text-sm" style={{ color: "var(--text-secondary)" }}>No orders yet.</p>}
          </div>
        </div>

        {/* Discounts & savings */}
        <div className="c4t-card !p-5">
          <h3 className="text-base font-semibold mb-3" style={{ color: "var(--text)" }}>Discounts Given</h3>
          <div className="space-y-3">
            {[
              { label: "Promo Codes", value: discounts.promo_codes_usd, color: "#FF1F87" },
              { label: "Vouchers", value: discounts.vouchers_usd, color: "#6366f1" },
              { label: "Store Credit", value: discounts.store_credit_usd, color: "#10b981" },
            ].map(d => (
              <div key={d.label} className="flex items-center justify-between py-2 border-b last:border-0" style={{ borderColor: "#f3f4f6" }}>
                <span className="text-sm" style={{ color: "var(--text)" }}>{d.label}</span>
                <span className="text-sm font-semibold" style={{ color: d.color }}>
                  {revCur === "JMD"
                    ? `J$${Math.round(d.value * (exchangeRate || 160)).toLocaleString()}`
                    : fmtUSD(d.value)}
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between pt-1">
              <span className="text-sm font-semibold" style={{ color: "var(--text)" }}>Total Savings Given</span>
              <span className="text-sm font-bold" style={{ color: "#ef4444" }}>
                {revCur === "JMD"
                  ? `J$${Math.round((discounts.promo_codes_usd + discounts.vouchers_usd + discounts.store_credit_usd) * (exchangeRate || 160)).toLocaleString()}`
                  : fmtUSD(discounts.promo_codes_usd + discounts.vouchers_usd + discounts.store_credit_usd)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Recharts bar chart extracted to avoid hooks-in-callback issues
const RechartsBarChart = ({ data, revCur, fmtRev }) => {
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="rounded-xl shadow-lg p-3 text-sm" style={{ background: "#fff", border: "1px solid #e5e7eb" }}>
        <p className="font-semibold mb-1" style={{ color: "var(--text)" }}>{label}</p>
        {payload.map(p => (
          <p key={p.name} style={{ color: p.color }}>{p.name}: {fmtRev(p.value)}</p>
        ))}
      </div>
    );
  };
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }} barCategoryGap="30%">
        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
        <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false}
          tickFormatter={v => revCur === "JMD" ? `J$${(v/1000).toFixed(0)}k` : `$${(v/1000).toFixed(1)}k`} />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
        <Bar dataKey="Physical" stackId="a" fill="#FF1F87" radius={[0, 0, 0, 0]} />
        <Bar dataKey="Digital" stackId="a" fill="#6366f1" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
};


const DigitalProductsTab = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(null); // product id being uploaded
  const [form, setForm] = useState({ name: "", description: "", price: "", currency: "JMD", cover_image: "", active: true });
  const [pendingPdf, setPendingPdf] = useState(null); // PDF file staged for upload after product creation
  const [dpage, setDpage] = useState(1);
  const [dpages, setDpages] = useState(1);
  const [dtotal, setDtotal] = useState(0);

  const load = () => {
    setLoading(true);
    api.get("/admin/digital-products?page=" + dpage + "&limit=10").then(r => {
      const d = r.data;
      if (Array.isArray(d)) { setProducts(d); setDtotal(d.length); setDpages(1); }
      else { setProducts(d.items || []); setDtotal(d.total || 0); setDpages(d.pages || 1); }
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [dpage]);

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", description: "", price: "", currency: "JMD", cover_image: "", active: true });
    setPendingPdf(null);
    setShowForm(true);
  };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({ name: p.name, description: p.description || "", price: String(p.price), currency: "JMD", cover_image: p.cover_image || "", active: p.active });
    setPendingPdf(null);
    setShowForm(true);
  };
  const cancelForm = () => { setShowForm(false); setEditing(null); setPendingPdf(null); };

  const handleCoverImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      // Compress cover image before storing — keeps MongoDB doc well under 16MB limit
      const compressed = await compressImage(file, 800, 0.70);
      setForm(f => ({ ...f, cover_image: compressed }));
    } catch {
      // Fallback to raw if compression fails
      const reader = new FileReader();
      reader.onload = (ev) => setForm(f => ({ ...f, cover_image: ev.target.result }));
      reader.readAsDataURL(file);
    }
  };

  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      price: parseFloat(form.price) || 0,
      currency: "JMD",
      cover_image: form.cover_image || "",
      active: form.active,
    };
    try {
      let productId = editing;
      if (editing) {
        await api.put(`/admin/digital-products/${editing}`, payload);
      } else {
        const created = await api.post("/admin/digital-products", payload);
        productId = created.data.id;
      }

      // Upload PDF if one was selected
      if (pendingPdf && productId) {
        const fd = new FormData();
        fd.append("file", pendingPdf);
        try {
          await api.post(`/admin/digital-products/${productId}/upload-pdf`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          toast.success(editing ? "Product updated with new PDF" : "Product created and PDF uploaded");
        } catch (pdfErr) {
          const msg = pdfErr?.response?.data?.detail || "PDF upload failed";
          toast.error(`Product saved but PDF upload failed: ${msg}. Edit the product to try again.`);
        }
      } else {
        toast.success(editing ? "Product updated" : "Product created");
      }

      cancelForm();
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Unknown error";
      toast.error(`Failed to save product: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/digital-products/${id}`);
      toast.success("Product deleted");
      load();
    } catch { toast.error("Failed to delete product"); }
  };

  const uploadPDF = async (id, file) => {
    if (!file) return;
    setUploading(id);
    const formData = new FormData();
    formData.append("file", file);
    try {
      await api.post(`/admin/digital-products/${id}/upload-pdf`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("PDF uploaded successfully");
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Upload failed";
      toast.error(msg);
    } finally { setUploading(null); }
  };

  const toggleActive = async (p) => {
    try {
      await api.put(`/admin/digital-products/${p.id}`, { ...p, active: !p.active });
      load();
    } catch { toast.error("Failed to update product"); }
  };

  const [digitalView, setDigitalView] = useState("products"); // "products" | "sales-log"

  if (loading) return <div className="animate-pulse space-y-3">{[...Array(3)].map((_,i) => <div key={i} className="h-24 bg-gray-100 rounded-2xl" />)}</div>;

  return (
    <div>
      {/* Toggle row */}
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div className="flex items-center gap-1 bg-gray-100 rounded-2xl p-1">
          <button
            onClick={() => setDigitalView("products")}
            className={`px-4 py-1.5 rounded-xl text-sm font-bold transition-all ${digitalView === "products" ? "bg-white border-[2px] border-black shadow-[2px_2px_0_#000] text-[#FF1F87]" : "text-[#455A64] hover:text-black"}`}
          >Digital Products ({dtotal})</button>
          <button
            onClick={() => setDigitalView("sales-log")}
            className={`px-4 py-1.5 rounded-xl text-sm font-bold transition-all ${digitalView === "sales-log" ? "bg-white border-[2px] border-black shadow-[2px_2px_0_#000] text-[#FF1F87]" : "text-[#455A64] hover:text-black"}`}
          >Sales Log</button>
        </div>
        {digitalView === "products" && (
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                try { await api.post("/admin/digital-products/rewatermark-covers"); toast.success("Re-watermarking digital covers..."); }
                catch { toast.error("Failed"); }
              }}
              className="c4t-pill !py-1.5 !px-3 !text-sm flex items-center gap-1"
            ><RefreshCw className="w-3 h-3" /> Re-watermark Covers</button>
            <button onClick={openNew} className="c4t-btn flex items-center gap-2 !py-2 !px-4">
              <Plus className="w-4 h-4" /> Add Product
            </button>
          </div>
        )}
      </div>



      {digitalView === "products" && (<>
      {/* Add/Edit Form */}
      {showForm && (
        <div className="c4t-card mb-6 border-2 border-[#FF1F87]">
          <h3 className="font-['Fredoka'] font-bold text-lg mb-4">{editing ? "Edit" : "New"} Digital Product</h3>
          <form onSubmit={save} className="space-y-4">
            <div>
              <label className="c4t-label">Product Name *</label>
              <input className="c4t-input" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Grade 3 Maths Worksheets" />
            </div>
            <div>
              <label className="c4t-label">Price (J$) *</label>
              <input type="number" step="any" min="0" required className="c4t-input"
                value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                placeholder="e.g. 750" />
            </div>
            <div>
              <label className="c4t-label">Description</label>
              <textarea className="c4t-input" rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="What's included in this download?" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="c4t-label">Cover Image <span className="text-xs font-normal text-[#455A64]">(thumbnail shown in shop)</span></label>
                <input type="file" accept="image/png,image/jpg,image/jpeg,image/webp" className="c4t-input !py-2 !text-sm" onChange={handleCoverImage} />
                {form.cover_image && (
                  <div className="mt-2 flex items-center gap-3">
                    <img src={form.cover_image} alt="preview" className="w-16 h-16 object-cover rounded-xl border-2 border-black" />
                    <button type="button" onClick={() => setForm(f => ({ ...f, cover_image: "" }))} className="c4t-pill !py-1 !px-3 !text-xs !bg-red-50 !text-red-600">Remove</button>
                  </div>
                )}
              </div>
              <div>
                <label className="c4t-label">PDF File <span className="text-xs font-normal text-[#455A64]">(the product customers download)</span></label>
                <input type="file" accept="application/pdf" className="c4t-input !py-2 !text-sm" onChange={e => setPendingPdf(e.target.files[0] || null)} />
                {pendingPdf && <p className="text-xs text-[#065f46] mt-1 font-medium">✓ {pendingPdf.name} — will upload on save</p>}
                {editing && !pendingPdf && <p className="text-xs text-[#455A64] mt-1">Leave blank to keep existing PDF</p>}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <input type="checkbox" id="dp-active" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} className="w-4 h-4" />
              <label htmlFor="dp-active" className="text-sm font-medium">Active (visible in Digital Shop)</label>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={saving} className="c4t-btn !py-2 !px-5 disabled:opacity-60 disabled:cursor-not-allowed">
                {saving ? "Saving..." : (editing ? "Save Changes" : "Create Product")}
              </button>
              <button type="button" onClick={cancelForm} disabled={saving} className="c4t-pill !py-2 !px-5 disabled:opacity-50">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Product list */}
      {products.length === 0 ? (
        <div className="c4t-card text-center py-16">
          <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="font-['Fredoka'] font-bold text-gray-400">No digital products yet. Click Add Product to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {products.map(p => (
            <div key={p.id} className={`c4t-card flex flex-wrap gap-4 items-start ${!p.active ? "opacity-60" : ""}`}>
              {/* Cover thumbnail */}
              <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#FFF9F2] border-2 border-black/10 flex items-center justify-center shrink-0">
                {p.cover_image ? <img src={p.cover_image} alt={p.name} className="w-full h-full object-cover" /> : <FileText className="w-8 h-8 text-[#FF1F87]/30" />}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-['Fredoka'] font-bold text-lg">{p.name}</p>
                  <span className="text-xs px-2 py-0.5 rounded-full border font-medium" style={p.active ? { background: "#d1fae5", color: "#065f46", borderColor: "#6ee7b7" } : { background: "#f3f4f6", color: "#6b7280", borderColor: "#d1d5db" }}>
                    {p.active ? "Active" : "Hidden"}
                  </span>
                  {p.file_key && (
                    <span className="text-xs px-2 py-0.5 rounded-full border font-medium" style={{ background: "#dbeafe", color: "#1e40af", borderColor: "#93c5fd" }}>
                      PDF ✓
                    </span>
                  )}
                  {!p.file_key && (
                    <span className="text-xs px-2 py-0.5 rounded-full border font-medium" style={{ background: "#fef3c7", color: "#92400e", borderColor: "#fcd34d" }}>
                      No PDF yet
                    </span>
                  )}
                </div>
                <p className="text-sm c4t-desc mt-0.5 line-clamp-1">{p.description}</p>
                <p className="text-[#FF1F87] font-bold mt-1">{p.currency === "JMD" ? "J$" : "$"}{p.price.toFixed(2)} <span className="text-xs font-normal text-[#455A64]">{p.currency || "USD"}</span></p>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 shrink-0">
                {/* PDF Upload */}
                <label className={`c4t-pill !py-1.5 !px-3 !text-xs cursor-pointer flex items-center gap-1 ${uploading === p.id ? "opacity-60 pointer-events-none" : ""}`}>
                  {uploading === p.id ? "Uploading…" : (p.file_key ? "Replace PDF" : "Upload PDF")}
                  <input type="file" accept="application/pdf" className="hidden" disabled={uploading === p.id}
                    onChange={e => uploadPDF(p.id, e.target.files[0])} />
                </label>
                <button onClick={() => toggleActive(p)} className="c4t-pill !py-1.5 !px-3 !text-xs flex items-center gap-1">
                  {p.active ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  {p.active ? "Hide" : "Show"}
                </button>
                <button onClick={() => openEdit(p)} className="c4t-pill !py-1.5 !px-3 !text-xs flex items-center gap-1">
                  <Pencil className="w-3 h-3" /> Edit
                </button>
                <button onClick={() => deleteProduct(p.id, p.name)} className="c4t-pill !py-1.5 !px-3 !text-xs !bg-red-50 !text-red-600 hover:!bg-red-100 flex items-center gap-1">
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {!loading && dpages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-6">
          <button onClick={() => { setDpage(x => Math.max(1, x - 1)); document.querySelector("main")?.scrollTo({ top: 0, behavior: "smooth" }); }} disabled={dpage <= 1} className="c4t-pill !py-1.5 !px-4 disabled:opacity-40">Prev</button>
          <span className="text-sm font-bold">Page {dpage} of {dpages}</span>
          <button onClick={() => { setDpage(x => Math.min(dpages, x + 1)); document.querySelector("main")?.scrollTo({ top: 0, behavior: "smooth" }); }} disabled={dpage >= dpages} className="c4t-pill !py-1.5 !px-4 disabled:opacity-40">Next</button>
        </div>
      )}
      </>)}
      {digitalView === "sales-log" && <DigitalSalesLog />}
    </div>
  );
};


function DigitalSalesLog() {
  const [orders, setOrders] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const fmtUSD = (v) => `$${Number(v || 0).toFixed(2)} USD`;

  React.useEffect(() => {
    api.get("/admin/digital-orders")
      .then(r => setOrders(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this sale record?")) return;
    try {
      await api.delete(`/admin/digital-orders/${id}`);
      setOrders(prev => prev.filter(o => o.id !== id));
      toast.success("Record deleted");
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-['Fredoka'] font-bold">Sales Log ({loading ? "…" : orders.length})</h3>
      </div>
      {loading ? (
        <div className="space-y-2">{[...Array(3)].map((_,i) => <div key={i} className="h-12 bg-gray-100 rounded-xl animate-pulse" />)}</div>
      ) : orders.length === 0 ? (
        <p className="c4t-desc text-sm">No digital sales yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b-2 border-black text-left">
                <th className="pb-2 pr-4 font-bold">Date</th>
                <th className="pb-2 pr-4 font-bold">Customer</th>
                <th className="pb-2 pr-4 font-bold">Items</th>
                <th className="pb-2 pr-4 font-bold">Total</th>
                <th className="pb-2 font-bold">Status</th>
                <th className="pb-2" />
              </tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.id} className="border-b border-black/10 hover:bg-gray-50">
                  <td className="py-2 pr-4 text-xs c4t-desc">{new Date(o.created_at).toLocaleDateString()}</td>
                  <td className="py-2 pr-4 font-medium">{o.user_email || o.email || "—"}</td>
                  <td className="py-2 pr-4 c4t-desc text-xs">{(o.items || []).map(i => i.name).join(", ") || o.product_name || "—"}</td>
                  <td className="py-2 pr-4 font-bold text-[#FF1F87]">{fmtUSD(o.amount || o.total_usd || o.amount_usd || 0)}</td>
                  <td className="py-2 pr-4">
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={o.status === "completed" ? {background:"#d1fae5",color:"#065f46"} : {background:"#fef3c7",color:"#92400e"}}>{o.status || "completed"}</span>
                  </td>
                  <td className="py-2">
                    <button onClick={() => del(o.id)} className="c4t-pill !py-0.5 !px-2 !text-xs !bg-red-50 !text-red-600"><Trash2 className="w-3 h-3" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


const ReviewsTab = () => {
  const [reviews, setReviews] = useState([]);
  const [rPage, setRPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/reviews").then(r => setReviews(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const deleteReview = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Review deleted");
      load();
    } catch { toast.error("Failed to delete review"); }
  };

  if (loading) return <div className="animate-pulse space-y-3">{[...Array(4)].map((_,i) => <div key={i} className="h-20 bg-gray-100 rounded-2xl" />)}</div>;

  return (
    <div>
      <h2 className="text-2xl font-['Fredoka'] font-bold mb-6">Reviews ({reviews.length})</h2>
      {reviews.length === 0 ? (
        <p className="c4t-desc">No reviews yet.</p>
      ) : (
        <div className="space-y-3">
          {reviews.slice((rPage-1)*10, rPage*10).map(rv => (
            <div key={rv.id} className="c4t-card">
              <div className="flex flex-wrap justify-between gap-2">
                <div>
                  <p className="font-bold">{rv.reviewer_name}
                    <span className="text-sm font-normal c4t-desc ml-2">{rv.reviewer_email}</span>
                  </p>
                  <div className="flex gap-0.5 mt-1">
                    {[...Array(5)].map((_,i) => (
                      <Star key={i} className={`w-4 h-4 ${i < rv.rating ? "fill-[#FFD166] text-[#FFD166]" : "text-gray-300"}`} />
                    ))}
                    <span className="ml-1 text-xs c4t-desc">{rv.rating}/5</span>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <p className="text-xs c4t-desc">{new Date(rv.created_at).toLocaleDateString("en-US",{year:"numeric",month:"short",day:"numeric"})}</p>
                  <button onClick={() => deleteReview(rv.id)} className="c4t-pill !py-1 !px-3 !bg-red-50 !text-red-600 hover:!bg-red-100 shrink-0">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
              <p className="mt-2 text-sm">{rv.text}</p>
            </div>
          ))}
      {!loading && Math.ceil(reviews.length/10) > 1 && (
        <div className="flex items-center justify-center gap-3 mt-6">
          <button onClick={() => { setRPage(x => Math.max(1, x - 1)); document.querySelector("main")?.scrollTo({ top: 0, behavior: "smooth" }); }} disabled={rPage <= 1} className="c4t-pill !py-1.5 !px-4 disabled:opacity-40">Prev</button>
          <span className="text-sm font-bold">Page {rPage} of {Math.ceil(reviews.length/10)}</span>
          <button onClick={() => { setRPage(x => Math.min(Math.ceil(reviews.length/10), x + 1)); document.querySelector("main")?.scrollTo({ top: 0, behavior: "smooth" }); }} disabled={rPage >= Math.ceil(reviews.length/10)} className="c4t-pill !py-1.5 !px-4 disabled:opacity-40">Next</button>
        </div>
      )}
        </div>
      )}
    </div>
  );
};


const RealShoppersTab = () => {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/shopper-photos").then(r => setPhotos(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    let uploaded = 0;
    for (const file of files) {
      await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = async (ev) => {
          try {
            await api.post("/admin/shopper-photos", { image: ev.target.result });
            uploaded++;
          } catch { toast.error(`Failed to upload ${file.name}`); }
          resolve();
        };
        reader.readAsDataURL(file);
      });
    }
    setUploading(false);
    toast.success(`${uploaded} photo${uploaded !== 1 ? "s" : ""} uploaded`);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this photo from the Real Shoppers section?")) return;
    try {
      await api.delete(`/admin/shopper-photos/${id}`);
      toast.success("Photo removed");
      load();
    } catch { toast.error("Failed to remove photo"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-['Fredoka'] font-bold">Real Shoppers</h2>
          <p className="c4t-desc text-sm mt-1">Upload photos of happy shoppers with their products — they scroll across the homepage.</p>
        </div>
        <label className={`c4t-btn flex items-center gap-2 cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
          <Upload className="w-4 h-4" />
          {uploading ? "Uploading..." : "Upload Photos"}
          <input type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} disabled={uploading} />
        </label>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="aspect-square bg-gray-100 rounded-2xl animate-pulse" />)}
        </div>
      ) : photos.length === 0 ? (
        <div className="c4t-card text-center py-16">
          <Camera className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-lg font-['Fredoka'] font-bold text-gray-400">No photos yet</p>
          <p className="c4t-desc text-sm mt-1">Upload some shopper photos to get started!</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.map(p => (
            <div key={p.id} className="relative group rounded-2xl overflow-hidden border-[3px] border-black aspect-square shadow-[4px_4px_0_#000]">
              <img src={p.image} alt="Shopper" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                <button
                  onClick={() => remove(p.id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity bg-[#FF1F87] text-white rounded-full p-2 border-2 border-white shadow-lg"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <span className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">
                {new Date(p.created_at).toLocaleDateString()}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("products");
  const [adminName, setAdminName] = useState("Admin");
  const [stats, setStats] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    document.title = "Admin – Cheap4Teach";
    const token = localStorage.getItem("c4t_admin_token");
    if (token) {
      api.get("/admin/verify")
        .then(r => { setAuthed(true); setAdminName(r.data.admin_name || "Admin"); })
        .catch(() => setAdminToken(null));
    }
  }, []);

  useEffect(() => {
    if (!authed) return;
    const loadStats = () => api.get("/admin/stats").then(r => setStats(r.data)).catch(() => {});
    loadStats();
    const id = setInterval(loadStats, 15000);
    return () => clearInterval(id);
  }, [authed]);

  if (!authed) return <Login onLogin={(name) => { setAuthed(true); setAdminName(name || "Admin"); }} />;

  const tabs = [
    { id: "products", label: "Products", icon: <Package className="w-4 h-4" /> },
    { id: "bundles", label: "Bundles", icon: <Layers className="w-4 h-4" /> },
    { id: "orders", label: "Orders", icon: <ShoppingBag className="w-4 h-4" /> },
    { id: "users", label: "Customers", icon: <Users className="w-4 h-4" /> },
    { id: "messages", label: "Messages", icon: <Mail className="w-4 h-4" />, badge: stats?.unread_messages > 0 },
    { id: "promos", label: "Promo Codes", icon: <Tag className="w-4 h-4" /> },
    { id: "vouchers", label: "Vouchers", icon: <Gift className="w-4 h-4" /> },
    { id: "notifications", label: "Notifications", icon: <Megaphone className="w-4 h-4" /> },
    { id: "reviews", label: "Reviews", icon: <Star className="w-4 h-4" /> },
    { id: "digital-products", label: "Digital Shop", icon: <FileText className="w-4 h-4" /> },
    { id: "real-shoppers", label: "Real Shoppers", icon: <Camera className="w-4 h-4" /> },
    { id: "reports", label: "Reports", icon: <BarChart2 className="w-4 h-4" /> },
    { id: "sections", label: "Sections", icon: <Grid3x3 className="w-4 h-4" /> },
    { id: "category-images", label: "Category Images", icon: <Layers className="w-4 h-4" /> },
    { id: "content", label: "Content", icon: <FileText className="w-4 h-4" /> },
  ];

  const handleTabChange = (id) => {
    setTab(id);
    setSidebarOpen(false);
    if (id === "messages") {
      api.post("/admin/contact-messages/mark-read").catch(() => {})
        .finally(() => api.get("/admin/stats").then(r => setStats(r.data)).catch(() => {}));
    } else {
      api.get("/admin/stats").then(r => setStats(r.data)).catch(() => {});
    }
  };
  const handleLogout = () => { setAdminToken(null); setAuthed(false); };

  return (
    <div className="h-screen overflow-hidden flex" style={{ background: "var(--bg)" }} data-testid="admin-dashboard">
      {/* ── Sidebar overlay (mobile) ── */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── Sidebar ── */}
      <aside
        className={`fixed top-0 left-0 h-screen w-56 z-50 flex flex-col shadow-2xl transition-transform duration-200
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 lg:static lg:z-auto lg:h-full lg:shrink-0`}
        style={{ background: "#ffffff", borderRight: "1px solid #e5e7eb" }}
      >
        {/* Logo */}
        <div className="flex items-center gap-2 px-5 py-5 border-b" style={{ borderColor: "#e5e7eb" }}>
          <span className="text-2xl font-['Fredoka'] font-bold" style={{ color: "#FF1F87" }}>Cheap4Teach</span>
          <button className="ml-auto lg:hidden" onClick={() => setSidebarOpen(false)}>
            <X className="w-5 h-5" style={{ color: "var(--text-secondary)" }} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => handleTabChange(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all relative`}
              style={tab === t.id
                ? { background: "#FF1F87", color: "#fff" }
                : { color: "var(--text)", background: "transparent" }
              }
            >
              {t.icon}
              <span>{t.label}</span>
              {t.badge && <span className="ml-auto w-2 h-2 rounded-full bg-red-500 shrink-0" />}
            </button>
          ))}
        </nav>

        {/* Settings + Logout */}
        <div className="p-3 border-t flex items-center justify-between gap-2" style={{ borderColor: "#e5e7eb" }}>
          <button
            onClick={() => handleTabChange("settings")}
            data-testid="admin-tab-settings"
            title="Settings"
            className="flex items-center justify-center w-10 h-10 rounded-2xl transition-all shrink-0 hover:opacity-90"
            style={tab === "settings" ? { background: "#FF1F87", color: "#fff" } : { background: "#f3f4f6", color: "#455A64" }}
          >
            <SettingsIcon className="w-4 h-4" />
          </button>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all hover:!bg-red-50 hover:!text-red-500"
            style={{ color: "var(--text-secondary)" }}
          >
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </aside>

      {/* ── Main content ── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top header */}
        <header className="sticky top-0 z-30 flex items-center gap-4 px-6 py-4 border-b"
          style={{ background: "#ffffff", borderColor: "#e5e7eb" }}>
          <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
            <Menu className="w-5 h-5" style={{ color: "var(--text)" }} />
          </button>
          <div className="flex-1">
            <p className="text-xs font-medium" style={{ color: "var(--text-secondary)" }}>
              {new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
            </p>
            <h1 className="text-xl sm:text-2xl font-['Fredoka'] font-bold leading-tight" style={{ color: "var(--text)" }}>
              Welcome back, <span style={{ color: "#FF1F87" }}>{adminName}</span>!
            </h1>
          </div>
          <a href="/" target="_blank" rel="noopener noreferrer"
            className="c4t-pill !py-1.5 !px-3 !text-xs hidden sm:flex items-center gap-1">
            <Home className="w-3 h-3" /> View Store
          </a>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6 overflow-y-auto min-h-0">
          <StatsCards stats={stats} />
          {tab === "products" && <ProductsTab />}
          {tab === "bundles" && <BundlesTab />}
          {tab === "orders" && <OrdersTab />}
          {tab === "users" && <UsersTab />}
          {tab === "messages" && <MessagesTab />}
          {tab === "promos" && <PromosTab />}
          {tab === "vouchers" && <VouchersTab />}
          {tab === "notifications" && <NotificationsTab />}
          {tab === "reviews" && <ReviewsTab />}
          {tab === "digital-products" && <DigitalProductsTab />}
          {tab === "real-shoppers" && <RealShoppersTab />}
          {tab === "reports" && <ReportsTab />}
          {tab === "sections" && <SectionsTab />}
          {tab === "category-images" && <CategoryImagesTab />}
          {tab === "content" && <ContentTab />}
          {tab === "settings" && <SettingsTab />}
        </main>
      </div>
    </div>
  );
}
